// Domínio puro da malha aérea (sem React).
// Parse/merge/pareamento das opções de ida e retorno.
import { moeda } from './util';

// Campos de UM lado (ida ou ret). A ordem importa: remapLado() renomeia
// POSICIONALMENTE IDA_KEYS[i] → RET_KEYS[i], então as duas listas têm que
// permanecer alinhadas item a item.
export const IDA_KEYS = [
    'idaOrigemIata', 'idaOrigemCidade', 'idaOrigemAeroporto',
    'idaDestinoIata', 'idaDestinoCidade', 'idaDestinoAeroporto',
    'idaData', 'idaCia', 'idaVoo', 'idaSaida', 'idaChegada', 'tarifaIda', 'taxasIda',
];
export const RET_KEYS = [
    'retOrigemIata', 'retOrigemCidade', 'retOrigemAeroporto',
    'retDestinoIata', 'retDestinoCidade', 'retDestinoAeroporto',
    'retData', 'retCia', 'retVoo', 'retSaida', 'retChegada', 'tarifaRet', 'taxasRet',
];

// Campos de valor de um grupo do Aéreo. Espelha EventoMalhaGrupo::CAMPOS_VALOR no
// accessapi — as duas listas andam juntas. Na sobrescrita de um trecho, `ida` e `retorno`
// são da solicitação (o back grava vazio e vale o do trecho); `quantidadePassageiros` o
// Aéreo ajusta nesta malha.
export const CAMPOS_GRUPO = [
    // A origem a que o trecho pertence NESTA malha. É o único campo aqui que não é texto de rota
    // nem data: entra porque a edição do Aéreo pode repontar o trecho para outra origem, e o
    // Reverter precisa saber qual era a de antes.
    'origem_id',
    'origem', 'origemHerdada', 'origemVolta', 'origemVoltaHerdada',
    'destino', 'destinoHerdado', 'destinoVolta', 'destinoVoltaHerdado',
    'ida', 'retorno',
    'dataIda', 'horaSaidaIdaDe', 'horaSaidaIdaAte', 'horaChegadaIdaDe', 'horaChegadaIdaAte',
    'dataRetorno', 'horaSaidaRetornoDe', 'horaSaidaRetornoAte', 'horaChegadaRetornoDe', 'horaChegadaRetornoAte',
    'quantidadePassageiros',
    // A observação também é sobrescrevível: vazia vale a do RSVP, e o texto dele continua à vista
    // no (i) do rótulo. Entra aqui para ser carregada, gravada e devolvida pelo Reverter.
    'observacao',
];

// Direções pedidas por um trecho ou grupo. Ausente conta como pedida: é o default da
// coluna, e é como todo vínculo era tratado antes de a direção existir.
export const pedeIda = (s) => s?.ida !== false && s?.ida !== 0;
export const pedeRetorno = (s) => s?.retorno !== false && s?.retorno !== 0;

/**
 * A data que vale para um vínculo: a dele, e vazia vale a da solicitação. O registro guarda só o
 * que é PRÓPRIO — é o vazio que diz "esta direção herda" —, e quem precisa do valor resolvido pede
 * aqui, como as pontas pedem a `pontasDoVinculo`. Resolver GRAVANDO o herdado dentro do trecho
 * faria o formulário abrir com a data da solicitação parecendo escolha dele, e o switch da direção
 * ligado; é a mesma regra da solicitação, e ela mora num lugar só.
 */
export const dataDoVinculo = (s, perna) => {
    const texto = (v) => String(v ?? '').trim();
    return texto(perna === 'ida' ? s?.dataIda : s?.dataRetorno)
        || texto(perna === 'ida' ? s?.dataIdaPedido : s?.dataRetornoPedido);
};

// A opção traz algum lado ('ida' | 'ret')? Olha as pontas da rota, não preço nem cia.
export const opcaoTemLado = (op, lado) => ['OrigemIata', 'OrigemCidade', 'OrigemAeroporto', 'DestinoIata', 'DestinoCidade', 'DestinoAeroporto']
    .some(suf => String(op?.[lado + suf] ?? '').trim() !== '');

/**
 * Vínculo efetivo de uma opção: grupo avulso do Aéreo, trecho ou origem sem trecho
 * (`origem_id`). Só um dos três fica gravado; a cadeia só desempata.
 *
 * TODA leitura do vínculo passa por aqui. Quando só o board conhecia `grupo` e o
 * resto do arquivo lia `op.trecho` cru, a opção do grupo aparecia na coluna
 * certa mas contava como órfã: o grupo ficava "sem opção" para sempre, o pax não
 * era herdado, as colunas não eram verificadas e o "Vincular órfãs" roubava a
 * opção de volta para um trecho.
 */
export const vinculoDe = (op) => {
    if (op == null) return null;
    const v = [op.grupo, op.trecho, op.origem_id].find(x => x != null && x !== '');
    return v == null ? null : String(v);
};

// O que o vínculo é por baixo da edição do Aéreo. `base` vem de EventoMalhaAerea::vinculos();
// sem ela (grupo criado na tela), o tipo diz.
export const baseDoVinculo = (s) => s?.base ?? (s?.tipo === 'grupo' ? 'grupo' : (s?.tipo === 'origem' ? 'origem' : 'trecho'));

// A opção ligada a UM vínculo, ou órfã com null: grava a coluna da base e zera as outras duas.
export const comVinculo = (op, vinculo) => {
    const limpa = { ...op, grupo: null, trecho: null, origem_id: null };
    if (!vinculo) return limpa;
    const coluna = { grupo: 'grupo', origem: 'origem_id' }[baseDoVinculo(vinculo)] ?? 'trecho';
    return { ...limpa, [coluna]: vinculo.id };
};

export function createEmptyOpcao() {
    return {
        opcao: '',
        idaOrigemIata: '', idaOrigemCidade: '', idaOrigemAeroporto: '',
        idaDestinoIata: '', idaDestinoCidade: '', idaDestinoAeroporto: '',
        idaData: '', idaCia: '', idaVoo: '', idaSaida: '', idaChegada: '',
        retOrigemIata: '', retOrigemCidade: '', retOrigemAeroporto: '',
        retDestinoIata: '', retDestinoCidade: '', retDestinoAeroporto: '',
        retData: '', retCia: '', retVoo: '', retSaida: '', retChegada: '',
        tarifaIda: '', taxasIda: '', tarifaRet: '', taxasRet: '', total: '', tktPessoa: '', tktGrupo: '',
        numParticipantes: null, obs: '', trecho: null, grupo: null, origem_id: null,
        // Opção criada à mão não veio de arquivo nenhum: sem nacionalidade, ela se comporta como
        // nacional, com os lados soltos.
        nacionalidade: null,
    };
}

const temLado = (op, keys) => keys.some(k => String(op[k] ?? '').trim() !== '');
const pick = (op, keys) => keys.reduce((acc, k) => ({ ...acc, [k]: op[k] ?? '' }), {});

// Opção sem NENHUM dado digitado: a linha que o operador esvaziou à mão na tabela.
// Ela não deve ir ao banco nem sair no .xlsx do Modelo Cliente.
// `opcao` e `trecho` ficam de fora da conta porque são
// atribuídos pela própria tela (numeração e agrupamento), não digitados; tktPessoa
// e tktGrupo idem, são calculados.
export const opcaoVazia = (op) =>
    !temLado(op, IDA_KEYS) && !temLado(op, RET_KEYS)
    && ['total', 'obs', 'numParticipantes']
        .every(k => String(op?.[k] ?? '').trim() === '');

// "R$ 1.336,24" → 1336.24  |  "US$ 3310,00" → 3310  |  "" / "-" → 0
export const parseMoeda = (v) => {
    const limpo = String(v ?? '').replace(/[^\d,.-]/g, '');
    if (limpo === '' || limpo === '-') return 0;
    const num = parseFloat(limpo.replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(num) ? 0 : num;
};

// Soma todas as linhas (\n) de um campo de moeda. Itinerário multi-trecho empilha
// as tarifas por linha ("R$ X\nR$ Y"); o total continua sendo UM valor (grand sum).
export const somaMoeda = (v) => String(v ?? '').split('\n').reduce((s, l) => s + parseMoeda(l), 0);

// total = Σ(tarifaIda + taxasIda + tarifaRet + taxasRet), somando todas as linhas (BRL)
export const calcularTotal = (op) => {
    const campos = [op.tarifaIda, op.taxasIda, op.tarifaRet, op.taxasRet];
    const temAlgum = campos.some(v => String(v ?? '').trim() !== '');
    if (!temAlgum) return '';
    const soma = campos.reduce((s, v) => s + somaMoeda(v), 0);
    return `R$ ${moeda(soma)}`;
};

// Tarifa + taxas de um lado ('ida' | 'ret'), somando as linhas: a coluna única que o Aéreo pediu
// (05/10) na tela e no Modelo Cliente. É só exibição: os dois campos seguem gravados em separado.
// Vazio quando o lado não tem nenhum dos dois.
export const tarifaComTaxas = (op, lado) => {
    const campos = lado === 'ret' ? [op?.tarifaRet, op?.taxasRet] : [op?.tarifaIda, op?.taxasIda];
    if (!campos.some(v => String(v ?? '').trim() !== '')) return '';
    return `R$ ${moeda(campos.reduce((s, v) => s + somaMoeda(v), 0))}`;
};

// A cidade que vai antes do aeroporto, "(Natal) São Gonçalo do Amarante", para o RSVP saber a cidade
// de um aeroporto que ele não conhece pelo nome (05/10). Uma por linha do aeroporto (a conexão
// empilha uma linha por perna), e só quando as duas existem e são diferentes. null quando nenhuma
// linha tem o que mostrar.
export const cidadesDoAeroporto = (cidade, aeroporto) => {
    const cidades = String(cidade ?? '').split('\n').map(s => s.trim());
    const linhas = String(aeroporto ?? '').split('\n').map(s => s.trim())
        .map((a, i) => (a && cidades[i] && cidades[i].toLowerCase() !== a.toLowerCase() ? cidades[i] : ''));
    return linhas.some(Boolean) ? linhas : null;
};

// Colunas CALCULADAS (read-only, espelham as fórmulas do export Modelo Cliente):
// total numérico de uma opção (Σ tarifas/taxas) → base do TKT.
export const totalNumerico = (op) =>
    somaMoeda(op?.tarifaIda) + somaMoeda(op?.taxasIda) + somaMoeda(op?.tarifaRet) + somaMoeda(op?.taxasRet);

// TKT médio por pessoa do grupo = média dos totais das opções (Σ total ÷ qtd) — = export V.
export const tktPorPessoaNum = (grupoOps) => {
    const ops = (grupoOps ?? []).filter(Boolean);
    return ops.length ? ops.reduce((s, op) => s + totalNumerico(op), 0) / ops.length : 0;
};

// TKT por grupo = TKT por pessoa × nº de participantes — = export W (V×X).
export const tktPorGrupoNum = (grupoOps, pax) => tktPorPessoaNum(grupoOps) * (Number(pax) || 0);

// Versões formatadas "R$ x" para exibir na tabela (vazio quando o grupo não tem opções).
export const tktPorPessoaStr = (grupoOps) => (grupoOps?.length ? `R$ ${moeda(tktPorPessoaNum(grupoOps))}` : '');
export const tktPorGrupoStr  = (grupoOps, pax) => (grupoOps?.length ? `R$ ${moeda(tktPorGrupoNum(grupoOps, pax))}` : '');

// IATA da origem/destino: "SAO - São PauloCongonhas" → "SAO"
export const iata = (t) => {
    const s = String(t ?? '').trim();
    const m = s.match(/^([A-Za-z]{3})\b/);
    return (m ? m[1] : s.slice(0, 3)).toUpperCase();
};

// Separa "SAO - São PauloGuarulhos" → { iata, cidade, aeroporto } — espelho de
// EventoAereoCasadorLocal::partesLocal. Sigla só com três maiúsculas seguidas de separador
// ou do fim: "Rio Branco" e "Foz do Iguaçu" não têm sigla. Cidade e aeroporto se separam
// na colagem minúscula→Maiúscula.
export const partesLocal = (texto) => {
    const s = String(texto ?? '').trim();
    const mIata = s.match(/^([A-Z]{3})(?=\s*(?:[^\p{L}\p{N}\s]|$))/u);
    const codigo = mIata ? mIata[1] : '';
    const resto = (mIata ? s.slice(3) : s).replace(/^[^\p{L}]+/u, '').trim();
    const mSplit = resto.match(/^(.*?\p{Ll})(\p{Lu}.*)$/u);
    if (mSplit) return { iata: codigo, cidade: mSplit[1].trim(), aeroporto: mSplit[2].trim() };
    return { iata: codigo, cidade: resto, aeroporto: '' };
};

// ida (A→B) casa com volta (B→A)? — usa os IATAs já extraídos
export const ehVoltaDe = (ida, volta) => {
    const id = String(ida.idaDestinoIata ?? '').toUpperCase();
    const io = String(ida.idaOrigemIata ?? '').toUpperCase();
    const ro = String(volta.retOrigemIata ?? '').toUpperCase();
    const rd = String(volta.retDestinoIata ?? '').toUpperCase();
    if (!id || !io) return false;
    return id === ro && io === rd;
};

/**
 * As quatro pontas de um vínculo — origem e destino da ida e da volta — a partir das flags da
 * solicitação. Cada ponta tem `base`, o lugar que o casador compara, e `valor`, o que a tela
 * mostra: "São Paulo (Congonhas)" quando o campo detalha o herdado em vez de trocá-lo.
 *
 * As regras são as da tela de solicitação, e esta é a cópia única delas:
 * - herdado ligado vale a referência (a origem do pedido, o destino do pedido); com texto
 *   digitado junto, o texto DETALHA a referência — o lugar continua sendo o de cima;
 * - herdado desligado vale o texto digitado, e sem texto a ponta fica sem lugar;
 * - a volta sem lugar próprio APONTA para a ida: volta por onde veio. Não é herança, é não ter
 *   sido definida;
 * - a volta com lugar próprio HERDA do ORIGINAL da solicitação — o destino do pedido para quem
 *   sai, a origem para quem chega. Nunca o que foi digitado na ida: trecho não herda de si mesmo.
 *
 * `referencias`: `origemPai` é o texto da origem do pedido a que o trecho pertence e `destino` é
 * o destino do pedido. Espelhado em EventoMalhaAerea::pontas() no accessapi.
 */
export function pontasDoVinculo(s, referencias = {}) {
    const ponta = (base, valor = base, detalhe = '') => ({
        base: String(base ?? '').trim(),
        valor: String(valor ?? '').trim(),
        // O texto que DETALHA o herdado ("Congonhas" em "São Paulo (Congonhas)"). O lugar é o de
        // cima, mas o casador pontua os dois: uma opção de Congonhas casa melhor do que uma de
        // Guarulhos, e era isso que o `origemPai` fazia antes de as flags existirem.
        detalhe: String(detalhe ?? '').trim(),
    });
    const campo = (digitado, ligado, ref) => {
        if (!digitado) return ligado ? ref : ponta('');
        if (!ligado || !ref.base) return ponta(digitado);
        // Digitou o MESMO lugar que herdaria: o lugar é um só. Repetir entre parênteses ("São Paulo
        // (São Paulo)") não acrescenta nada à tela, e no vínculo automático o mesmo texto contaria
        // duas vezes — valor igual não soma, se confirma.
        if (chaveOrigem(digitado) === chaveOrigem(ref.base)) return ponta(ref.base);
        return ponta(ref.base, `${ref.base} (${digitado})`, digitado);
    };
    const texto = (v) => String(v ?? '').trim();
    const pai = ponta(referencias.origemPai ?? s?.origemPai ?? '');
    const destinoPedido = ponta(referencias.destino ?? '');

    const origemIda = campo(texto(s?.origem), s?.origemHerdada, pai);
    const destinoIda = campo(texto(s?.destino), s?.destinoHerdado, destinoPedido);
    // HERDAR e APONTAR são coisas diferentes, e a volta usa as duas:
    // - sem lugar próprio ela APONTA, voltando por onde veio (espelha a ponta da ida). Não é
    //   herança, é não ter sido definida: o trecho vai e volta pelo mesmo caminho;
    // - com lugar próprio ela HERDA do ORIGINAL da solicitação — o destino do pedido para quem
    //   sai, a origem para quem chega. Nunca o que foi digitado na ida: detalhar a ida não pode
    //   mudar de onde a volta parte.
    const temCampo = (v) => texto(v) !== '';
    return {
        origemIda,
        destinoIda,
        origemVolta: temCampo(s?.origemVolta) ? campo(texto(s.origemVolta), s?.origemVoltaHerdada, destinoPedido) : destinoIda,
        destinoVolta: temCampo(s?.destinoVolta) ? campo(texto(s.destinoVolta), s?.destinoVoltaHerdado, pai) : origemIda,
    };
}

// A rota de um vínculo como o cabeçalho do trecho na tela da solicitação a escreve, e este é o lugar
// único dela: cada ponta sai como a tela mostra (`valor`, o lugar de cima com o detalhe entre
// parênteses), "São Paulo (Congonhas) ⇄ Salvador". Com a volta espelhando a ida, ⇄; com a volta saindo
// de onde a ida chega para outro lugar, "São Paulo ➝ Salvador ➝ Rio de Janeiro"; com um caminho em
// cada direção, "São Paulo ➝ Salvador · Porto Seguro ➝ São Paulo"; sem uma das direções ("Sem
// trecho"), só a pedida. Vazio quando nenhuma ponta tem lugar. A seta simples é a "➝", do mesmo peso
// das do "⇄". `pontas`: as do `pontasDe` ou do `pontasDoVinculo`.
export function rotaDoTrecho(pontas, { ida = true, retorno = true } = {}) {
    const [origemIda, destinoIda, origemVolta, destinoVolta] =
        [pontas?.origemIda, pontas?.destinoIda, pontas?.origemVolta, pontas?.destinoVolta].map(p => String(p?.valor ?? '').trim());
    if (!origemIda && !destinoIda && !origemVolta && !destinoVolta) return '';
    const lugar = (v) => v || '?';
    if (ida && !retorno) return `${lugar(origemIda)} ➝ ${lugar(destinoIda)}`;
    if (retorno && !ida) return `${lugar(origemVolta)} ➝ ${lugar(destinoVolta)}`;
    const voltaSaiDoDestino = chaveOrigem(origemVolta) === chaveOrigem(destinoIda);
    const voltaChegaNaOrigem = chaveOrigem(destinoVolta) === chaveOrigem(origemIda);
    if (voltaSaiDoDestino && voltaChegaNaOrigem) return `${lugar(origemIda)} ⇄ ${lugar(destinoIda)}`;
    if (voltaSaiDoDestino) return `${lugar(origemIda)} ➝ ${lugar(destinoIda)} ➝ ${lugar(destinoVolta)}`;
    return `${lugar(origemIda)} ➝ ${lugar(destinoIda)} · ${lugar(origemVolta)} ➝ ${lugar(destinoVolta)}`;
}

// A legenda embaixo da rota no cabeçalho do trecho, e o par dela: "Ida 12/03/2026 saída de 08:00 até
// 10:00 | Volta sem data | 3 passageiros". As duas direções, ou o "Sem trecho"; direção ou horário
// fechado não conta, e a data vazia fica a herdada do pedido. Os passageiros só entram quando o trecho
// tem quantidade própria. `t`: o trecho no estado de tela (`trechoParaTela`), com as marcas.
export function resumoDoTrecho(t, { dataIda = '', dataRetorno = '' } = {}) {
    const txt = (v) => String(v ?? '').trim();
    const janela = (h) => {
        const de = txt(t[h.de]);
        const ate = txt(t[h.ate]);
        if (de && ate) return `${h.curto} de ${de} até ${ate}`;
        if (de) return `${h.curto} a partir de ${de}`;
        return ate ? `${h.curto} até ${ate}` : '';
    };
    const direcao = (rotulo, aberta, campoData, herdada, horarios) => [
        rotulo,
        (aberta && txt(t[campoData])) || txt(herdada) || 'sem data',
        ...horarios.map(h => (aberta && t[h.abrir] ? janela(h) : '')),
    ].filter(Boolean).join(' ');
    const partes = [
        t.ida === false ? 'Sem trecho de Ida' : direcao('Ida', t.detalharIda, 'dataIda', dataIda, HORARIOS.ida),
        t.retorno === false ? 'Sem trecho de Volta' : direcao('Volta', t.detalharRetorno, 'dataRetorno', dataRetorno, HORARIOS.retorno),
    ];
    if (t.quantidadePassageiros != null) {
        partes.push(`${t.quantidadePassageiros} passageiro${t.quantidadePassageiros !== 1 ? 's' : ''}`);
    }
    return partes.join(' | ');
}

// Os dois locais do trecho, e o que acompanha cada perna deles: o texto, o badge do herdado e a
// marca de campo aberto. A marca de aberto é SÓ DA TELA — o que fica gravado é o texto; vazio quer
// dizer que a volta espelha a ida. Mora aqui porque a tela de solicitação e o modal da malha
// editam os mesmos campos, e `trechoEfetivo` precisa da mesma lista.
export const LOCAIS = {
    origem: {
        nome: 'Origem', plural: 'Origens',
        ida:   { perna: 'ida',   campo: 'origem',      herdado: 'origemHerdada',      aberto: 'origemIdaAberta' },
        volta: { perna: 'volta', campo: 'origemVolta', herdado: 'origemVoltaHerdada', aberto: 'origemVoltaAberta' },
    },
    destino: {
        nome: 'Destino', plural: 'Destinos',
        ida:   { perna: 'ida',   campo: 'destino',      herdado: 'destinoHerdado',      aberto: 'destinoIdaAberta' },
        volta: { perna: 'volta', campo: 'destinoVolta', herdado: 'destinoVoltaHerdado', aberto: 'destinoVoltaAberta' },
    },
};
export const TODOS_CAMPOS_LOCAL = [LOCAIS.origem.ida, LOCAIS.origem.volta, LOCAIS.destino.ida, LOCAIS.destino.volta];
// Abre o campo dessa perna nas duas pontas.
export const abrirPernaDosLocais = (perna) => ({ [LOCAIS.origem[perna].aberto]: true, [LOCAIS.destino[perna].aberto]: true });
// Trecho novo nasce com a ida aberta (mesmo vazia) e a volta fechada: o normal é a volta espelhar.
export const ABERTURAS_PADRAO = { origemIdaAberta: true, origemVoltaAberta: false, destinoIdaAberta: true, destinoVoltaAberta: false };

// Só o campo VISÍVEL vale — e visível é campo aberto de direção pedida. Fechado, ou de uma direção
// com "Sem trecho" marcado, o texto continua na tela mas não conta, e o Salvar grava vazio. É o
// mesmo contrato dos switches de horário, e é por isso que a conta mora num lugar só.
export const trechoEfetivo = (t) => {
    const efetivo = { ...t };
    TODOS_CAMPOS_LOCAL.forEach(c => {
        const pedida = c.perna === 'ida' ? pedeIda(t) : pedeRetorno(t);
        if (!pedida || !t[c.aberto]) efetivo[c.campo] = '';
    });
    return efetivo;
};

// Horários de cada direção: saída e chegada, cada um com De e Até, nas colunas por perna e limite
// (2026_09_16_120000). `abrir` é o switch, só da tela; fechado, os dois campos não vão ao banco.
export const HORARIOS = {
    ida: [
        { abrir: 'horarioSaidaIda', rotulo: 'Horário de saída', curto: 'saída', de: 'horaSaidaIdaDe', ate: 'horaSaidaIdaAte' },
        { abrir: 'horarioChegadaIda', rotulo: 'Horário de chegada', curto: 'chegada', de: 'horaChegadaIdaDe', ate: 'horaChegadaIdaAte' },
    ],
    retorno: [
        { abrir: 'horarioSaidaVolta', rotulo: 'Horário de saída', curto: 'saída', de: 'horaSaidaRetornoDe', ate: 'horaSaidaRetornoAte' },
        { abrir: 'horarioChegadaVolta', rotulo: 'Horário de chegada', curto: 'chegada', de: 'horaChegadaRetornoDe', ate: 'horaChegadaRetornoAte' },
    ],
};
export const TODOS_HORARIOS = [...HORARIOS.ida, ...HORARIOS.retorno];
// O que pertence a cada direção e só existe enquanto ela é detalhada na tela.
export const CAMPOS_IDA = ['dataIda', ...HORARIOS.ida.flatMap(h => [h.de, h.ate])];
export const CAMPOS_RETORNO = ['dataRetorno', ...HORARIOS.retorno.flatMap(h => [h.de, h.ate])];

// ---------------------------------------------------------------------------------------------
// As três regras de edição de um trecho. Puras, e por isso as mesmas nas duas telas: quem guarda o
// estado é cada uma, mas o que acontece a cada gesto é decidido aqui.
// ---------------------------------------------------------------------------------------------

/** Switch da direção: ligar também volta a PEDIR a direção (desmarca o "Sem trecho"). */
export const comDirecaoLigada = (t, dir, ligar) =>
    ({ ...t, [dir.abrir]: ligar, ...(ligar ? { [dir.chave]: true } : {}) });

/**
 * "Sem trecho": marcar desliga o switch daquela direção e volta a pedir a OUTRA — o trecho nunca
 * fica sem as duas, e é esta função que garante isso. Marcar a segunda TROCA qual está de fora, em
 * vez de não fazer nada: é o gesto de quem mudou de ideia sobre qual direção o trecho tem.
 *
 * A perna que sobrou ganha campo próprio na hora, nas duas pontas: sem isso ela
 * ficaria só com os badges apagados para clicar, já que os campos da direção que saiu deixam de
 * valer. Abrir campo vazio não muda o que vale — vazio com o herdado ligado continua valendo o
 * mesmo espelho. Desmarcar só volta a pedir a direção.
 */
export const comSemTrecho = (t, dir, sem) => (sem
    ? {
        ...t, [dir.chave]: false, [dir.abrir]: false,
        [dir.chave === 'ida' ? 'retorno' : 'ida']: true,
        ...abrirPernaDosLocais(dir.chave === 'ida' ? 'volta' : 'ida'),
    }
    : { ...t, [dir.chave]: true });

/** Apagar o texto do local RELIGA o badge do herdado. Escrever não mexe nele: desligar é no clique. */
export const comLocalDigitado = (t, campo, valor) => {
    const novo = { ...t, [campo.campo]: valor };
    return !valor.trim() && String(t?.[campo.campo] ?? '').trim() ? { ...novo, [campo.herdado]: true } : novo;
};

/**
 * Quais campos de local ficam abertos ao abrir o trecho. A abertura não é gravada: sai do que o
 * trecho tem, e só muda o que aparece — campo fechado e campo aberto vazio gravam a mesma coisa.
 *
 * A perna abre quando tem lugar próprio, e ainda:
 * - a volta abre quando é a única direção pedida, como o "Sem trecho de Ida" faz ao ser marcado;
 * - a ida abre quando a volta da mesma ponta não tem lugar próprio, para a ponta não ficar sem
 *   campo, e quando o badge dela está desligado: vazia e sem herdar, ela pede atenção.
 * Ida vazia que herda, com a volta própria, abre só a volta: é como a tela fica ao fechar a ida.
 *
 * @param {Object} t trecho no estado de tela (direções e badges já normalizados)
 */
export const aberturasDaTela = (t) => {
    const proprio = (campo) => String(t?.[campo] ?? '').trim() !== '';
    return Object.fromEntries(Object.values(LOCAIS).flatMap(({ ida, volta }) => [
        [ida.aberto, proprio(ida.campo) || !t[ida.herdado] || !(pedeRetorno(t) && proprio(volta.campo))],
        [volta.aberto, proprio(volta.campo) || !pedeIda(t)],
    ]));
};

/**
 * Um trecho gravado vira estado de TELA — valores normalizados mais as marcas do que está à vista.
 *
 * É o par do `trechoParaGravar`, e existe pelo mesmo motivo: as duas telas que editam trecho
 * precisam abrir no MESMO estado. Enquanto cada uma montava o seu, elas divergiram em silêncio —
 * o modal da malha abria as direções sempre e nascia com os badges de herança DESLIGADOS, quando
 * a regra é o contrário: a direção abre só se já tem valor, e o badge nasce ligado.
 *
 * As marcas não vêm do banco. O que está gravado é o texto; o que está à vista se deduz dele.
 */
export function trechoParaTela(t) {
    const txt = (v) => String(v ?? '').trim();
    const campos = ['origem', 'destino', 'origemVolta', 'destinoVolta', 'observacao',
        ...CAMPOS_IDA, ...CAMPOS_RETORNO];
    const tela = {
        ...t,
        ...campos.reduce((acc, c) => ({ ...acc, [c]: String(t?.[c] ?? '') }), {}),
        // Sem escolha gravada, o badge do herdado nasce LIGADO: campo vazio vale o herdado, e é o
        // clique que o desliga. Nulo virando falso era o que fazia o modal abrir sem os badges.
        origemHerdada: t?.origemHerdada == null || !!t.origemHerdada,
        destinoHerdado: t?.destinoHerdado == null || !!t.destinoHerdado,
        origemVoltaHerdada: t?.origemVoltaHerdada == null || !!t.origemVoltaHerdada,
        destinoVoltaHerdado: t?.destinoVoltaHerdado == null || !!t.destinoVoltaHerdado,
        // Direção ausente conta como pedida — é o default da coluna.
        ida: t?.ida == null || !!Number(t.ida),
        retorno: t?.retorno == null || !!Number(t.retorno),
        // Abre na tela a direção, e o horário, que JÁ TEM valor gravado — e só nesse caso. Aberta
        // sempre, ela diria que aquele voo tem valor próprio quando ele só herda.
        detalharIda: CAMPOS_IDA.some(c => txt(t?.[c])),
        detalharRetorno: CAMPOS_RETORNO.some(c => txt(t?.[c])),
        ...Object.fromEntries(TODOS_HORARIOS.map(h => [h.abrir, !!(txt(t?.[h.de]) || txt(t?.[h.ate]))])),
    };
    // Os campos de local abrem pelo estado já normalizado: a regra olha as direções e os badges.
    return { ...tela, ...aberturasDaTela(tela) };
}

/**
 * O trecho pronto para o banco: sai daqui o que é MARCA DE TELA e o que ela esconde.
 *
 * As marcas (`detalharIda`, `horarioSaidaIda`, `origemIdaAberta`…) dizem o que está à vista, e o
 * que não está não vale: campo fechado, janela fechada e direção sem detalhe gravam vazio. O texto
 * fica no estado da tela até aqui, para voltar se o switch reabrir antes do Salvar.
 *
 * Mora junto das outras regras porque a solicitação e o modal da malha editam o mesmo trecho — se
 * cada tela montasse o próprio payload, elas gravariam coisas diferentes do mesmo formulário.
 *
 * @param {Object} t trecho da tela, com as marcas
 * @param {Object} [opcoes.detalhes] as marcas, quando vivem fora do trecho (o modal guarda à parte)
 */
export function trechoParaGravar(t, { detalhes = null } = {}) {
    const marcas = detalhes ?? t;
    const limpo = trechoEfetivo({ ...t, ...(detalhes ?? {}) });
    TODOS_HORARIOS.forEach(h => {
        if (!marcas[h.abrir]) limpo[h.de] = limpo[h.ate] = null;
        delete limpo[h.abrir];
    });
    // Direção sem detalhe não leva data nem hora: vale a data do pedido.
    [['detalharIda', CAMPOS_IDA], ['detalharRetorno', CAMPOS_RETORNO]].forEach(([marca, campos]) => {
        if (!marcas[marca]) campos.forEach(c => { limpo[c] = null; });
        delete limpo[marca];
    });
    TODOS_CAMPOS_LOCAL.forEach(c => { delete limpo[c.aberto]; });
    return limpo;
}

// As oito janelas de horário de um trecho, na ordem em que a solicitação as mostra.
export const HORAS_DO_TRECHO = [
    'horaSaidaIdaDe', 'horaSaidaIdaAte', 'horaChegadaIdaDe', 'horaChegadaIdaAte',
    'horaSaidaRetornoDe', 'horaSaidaRetornoAte', 'horaChegadaRetornoDe', 'horaChegadaRetornoAte',
];

/**
 * O que de um trecho conta como "mexido" em relação ao pedido base: os locais e as flags deles,
 * as direções pedidas, as datas e as horas. FORA ficam a observação, que não casa com nada, e a
 * quantidade de passageiros, que se cruza entre trechos e tornaria a comparação frágil.
 *
 * Serve a duas coisas: a trava de duplicidade (dois trechos da mesma origem com a mesma
 * assinatura não podem coexistir) e o `trechoNeutro`. Espelhado em EventoAereoTrecho no accessapi.
 */
export const assinaturaDoTrecho = (t) => {
    const texto = (v) => String(v ?? '').trim().toLowerCase();
    const ligado = (v) => (v == null ? true : !!v);
    return JSON.stringify([
        texto(t?.origem), texto(t?.destino),
        ligado(t?.origemHerdada), ligado(t?.destinoHerdado),
        texto(t?.origemVolta), texto(t?.destinoVolta),
        ligado(t?.origemVoltaHerdada), ligado(t?.destinoVoltaHerdado),
        pedeIda(t), pedeRetorno(t),
        texto(t?.dataIda), texto(t?.dataRetorno),
        ...HORAS_DO_TRECHO.map(c => texto(t?.[c])),
    ]);
};

// O trecho que não muda NADA em relação ao pedido base da origem: herdou origem e destino sem
// detalhar, pede as duas direções e não tem data nem hora própria. A trava de duplicidade garante
// que existe no máximo um por origem — e é por isso que o vínculo automático pode desempatar a
// favor dele sem ambiguidade. A origem continua no painel de qualquer jeito: trecho muda a
// qualquer momento, origem não.
export const TRECHO_NEUTRO = assinaturaDoTrecho({});
export const trechoNeutro = (t) => !!t && assinaturaDoTrecho(t) === TRECHO_NEUTRO;

// No vínculo vindo do back os campos já chegam resolvidos (data do pedido no lugar da vazia), e
// aí a assinatura não serve: quem diz é a marca `neutro`, calculada lá sobre o trecho cru.
// Item montado na tela (grupo novo, edição não salva) cai na conta local.
export const ehTrechoNeutro = (s) => (s?.neutro != null ? !!s.neutro : trechoNeutro(s));

// Decompõe as opcoes em duas listas ALINHADAS por opção (mesmo índice = mesma linha).
// Lado ausente vira `null` (placeholder) — preserva o pareamento salvo, sem "subir" itens.
export function decompor(opcoes) {
    const idaItems = [];
    const retItems = [];
    opcoes.forEach((op, i) => {
        const meta = {
            opcao: op.opcao ?? '',
            numParticipantes: op.numParticipantes ?? null,
            tktPessoa: op.tktPessoa ?? '',
            tktGrupo: op.tktGrupo ?? '',
            obs: op.obs ?? '',
            total: op.total ?? '',
            // Vínculo da opção (segue a origem = lado IDA); sem isso o Aplicar do
            // modal zeraria o agrupamento (tudo viraria órfã). Os DOIS campos: a
            // opção de grupo avulso viaja em `grupo`, com `trecho` nula.
            trecho: op.trecho ?? null,
            grupo: op.grupo ?? null,
            origem_id: op.origem_id ?? null,
            // Nacional ou internacional, gravado na importação. No internacional a tarifa é do PAR
            // ida e volta, e é o que trava o par no modal — sem viajar no meta, o Aplicar devolveria
            // a opção sem ela e o par soltaria no próximo Ajustar.
            nacionalidade: op.nacionalidade ?? null,
            // A chave do banco e o destaque. Sem eles o Aplicar devolvia só linhas novas: o Salvar
            // apagava todas as opções e criava outras, travava quando alguma estava no evento, e o
            // destaque sumia.
            id: op.id ?? null,
            destaques: op.destaques ?? null,
        };
        idaItems.push(temLado(op, IDA_KEYS) ? { ...pick(op, IDA_KEYS), _id: `ida-${i}`, _meta: meta } : null);
        retItems.push(temLado(op, RET_KEYS) ? { ...pick(op, RET_KEYS), _id: `ret-${i}`, _meta: meta } : null);
    });
    return { idaItems, retItems };
}

// Ao importar: se as novas linhas são puras de um lado (só ida ou só retorno),
// preenche o lado faltante das linhas existentes; sobras viram linhas novas.
export function mesclarOuAnexar(prev, novas) {
    if (!novas.length) return prev;

    const novasTemIda = novas.some(r => temLado(r, IDA_KEYS));
    const novasTemRet = novas.some(r => temLado(r, RET_KEYS));
    const novasSoIda = novasTemIda && !novasTemRet;
    const novasSoRet = novasTemRet && !novasTemIda;

    if (novasSoRet) return mesclarPorRota(prev, novas, 'ret');
    if (novasSoIda) return mesclarPorRota(prev, novas, 'ida');
    return [...prev, ...novas]; // internacional (já pareado) → append
}

// Critério: rota (volta.origem = ida.destino e volta.destino = ida.origem).
// Dois passos: 1º casa por rota; 2º preenche restantes por ordem (fallback).
function mesclarPorRota(prev, novas, ladoNovo) {
    const keysNovo      = ladoNovo === 'ret' ? RET_KEYS : IDA_KEYS;
    const keysExistente = ladoNovo === 'ret' ? IDA_KEYS : RET_KEYS;
    const usado = new Array(novas.length).fill(false);

    const rows = prev.map(r => ({ ...r }));
    const precisam = [];
    rows.forEach((row, i) => {
        if (temLado(row, keysExistente) && !temLado(row, keysNovo)) precisam.push(i);
    });

    const preencher = (i, j) => {
        usado[j] = true;
        rows[i] = { ...rows[i], ...pick(novas[j], keysNovo) };
        rows[i].total = calcularTotal(rows[i]);
    };

    // Passo 1 — casar por rota
    precisam.forEach(i => {
        const row = rows[i];
        const j = novas.findIndex((n, k) => !usado[k] && (
            ladoNovo === 'ret' ? ehVoltaDe(row, n) : ehVoltaDe(n, row)
        ));
        if (j >= 0) preencher(i, j);
    });

    // Passo 2 — preencher restantes por ordem
    precisam.forEach(i => {
        if (temLado(rows[i], keysNovo)) return;
        const j = usado.findIndex(u => !u);
        if (j >= 0) preencher(i, j);
    });

    const sobras = novas.filter((_, k) => !usado[k]);
    return [...rows, ...sobras];
}

// Lista única de cidades de origem (ida), na ordem em que aparecem.
export function cidadesOrigem(opcoes) {
    return [...new Set((opcoes ?? []).map(o => String(o.idaOrigemCidade ?? '')))];
}

// Preserva a ordem atual das opções (a arrumação feita no modal/import) e apenas
// renumera a opção por cidade de origem (1-based).
export function organizar(opcoes) {
    const contador = {};
    return (opcoes ?? []).map(op => {
        const cidade = String(op.idaOrigemCidade ?? '');
        contador[cidade] = (contador[cidade] || 0) + 1;
        return { ...op, opcao: String(contador[cidade]) };
    });
}

// Numera a opção pela posição dentro do seu grupo de trecho (1-based), na ordem
// atual da lista; órfãs (sem trecho) seguem uma sequência própria. Preserva a ordem.
export function numerarPorGrupo(opcoes) {
    const cont = new Map();
    return (opcoes ?? []).map(op => {
        const sid = vinculoDe(op) ?? '__orfas__';
        cont.set(sid, (cont.get(sid) || 0) + 1);
        return { ...op, opcao: String(cont.get(sid)) };
    });
}

// Funde as duas listas índice-a-índice em linhas pareadas (tolera null; ignora linhas vazias).
// `pares` (opcional): quais LINHAS são par internacional no Ajustar malha. A nacionalidade passa
// a vir da linha, não do trecho: o `_meta` só acompanha o 1º trecho de cada lado, e reordenar as
// pernas de um par apagaria a marca dele; e o trecho que SAIU de um par leva o `_meta` do par
// junto, mas não faz mais parte da tarifa de ida e volta — vira nacional.
export function fundir(idaItems, retItems, pares = null) {
    const n = Math.max(idaItems.length, retItems.length);
    // O id da opção segue a IDA, como o vínculo: fica com a linha cuja ida veio dela. A volta que
    // ficou sem ida leva o id da opção de onde saiu só se nenhuma ida o levou, e cada id vai para uma
    // linha só — dois com o mesmo id gravariam as duas linhas na mesma opção.
    const idsDaIda = new Set(idaItems.map(it => it?._meta?.id).filter(v => v != null).map(String));
    const usados = new Set();
    // O destaque de cada lado vem da opção daquele lado; o da linha e o das colunas sem lado, da
    // opção que dá o resto da linha.
    const destaquesDe = (m, filtro) => Object.entries(m?.destaques || {}).filter(([k]) => filtro(k));
    const semLado = (k) => !IDA_KEYS.includes(k) && !RET_KEYS.includes(k);
    const novas = [];
    let pos = 0;
    for (let i = 0; i < n; i++) {
        const ida = idaItems[i];
        const ret = retItems[i];
        if (!ida && !ret) continue; // linha totalmente vazia → ignora
        const meta = ida?._meta ?? ret?._meta ?? {};
        const nacionalidade = !pares ? (meta.nacionalidade ?? null)
            : pares[i] ? 'internacional'
            : meta.nacionalidade === 'internacional' ? 'nacional' : (meta.nacionalidade ?? null);
        const candidato = ida ? ida._meta?.id
            : (ret?._meta?.id != null && !idsDaIda.has(String(ret._meta.id)) ? ret._meta.id : null);
        const id = candidato != null && !usados.has(String(candidato)) ? candidato : null;
        if (id != null) usados.add(String(id));
        // A volta que saiu da ida dela (a ida está em outra linha) não herda o vínculo da opção, que
        // foi decidido pela rota da ida. Vem sem vínculo e marcada, e quem aplica a liga pela rota da
        // própria volta (`vincularOrfas`), como o upload faz com a volta que sobra.
        const voltaSolta = !ida && ret?._meta?.id != null && idsDaIda.has(String(ret._meta.id));
        const destaques = Object.fromEntries([
            ...destaquesDe(meta, semLado),
            ...destaquesDe(ida?._meta, k => IDA_KEYS.includes(k)),
            ...destaquesDe(ret?._meta, k => RET_KEYS.includes(k)),
        ]);
        const merged = {
            ...createEmptyOpcao(),
            ...(ida ? pick(ida, IDA_KEYS) : {}),
            ...(ret ? pick(ret, RET_KEYS) : {}),
            // A posição na lista (ordem do drag-n-drop) vira o número da opção;
            // organizar() reordena por cidade + opção e renumera por origem.
            opcao: String(++pos),
            numParticipantes: meta.numParticipantes ?? null,
            tktPessoa: meta.tktPessoa ?? '',
            tktGrupo: meta.tktGrupo ?? '',
            obs: meta.obs ?? '',
            trecho: voltaSolta ? null : (meta.trecho ?? null),
            grupo: voltaSolta ? null : (meta.grupo ?? null),
            origem_id: voltaSolta ? null : (meta.origem_id ?? null),
            ...(voltaSolta ? { _voltaSolta: true } : {}),
            nacionalidade,
            ...(id != null ? { id } : {}),
            destaques: Object.keys(destaques).length ? destaques : null,
        };
        merged.total = calcularTotal(merged);
        novas.push(merged);
    }
    return novas;
}

// Resumo curto de um trecho para exibir no slot do modal de pareamento.
export function resumoTrecho(item, lado) {
    const p = lado === 'ida' ? 'ida' : 'ret';
    const origem  = (item[`${p}OrigemIata`] || item[`${p}OrigemAeroporto`] || '?');
    const destino = (item[`${p}DestinoIata`] || item[`${p}DestinoAeroporto`] || '?');
    const cia     = item[`${p}Cia`] ?? '';
    const voo     = item[`${p}Voo`] ?? '';
    const saida   = item[`${p}Saida`] ?? '';
    // data de saída sem ano: "18/06/2026" → "18/06" (pega a 1ª data quando há quebra)
    const dataSaida = (item[`${p}Data`] ?? '').split('\n')[0].split('/').slice(0, 2).join('/');

    const partes = [`${origem} → ${destino}`];
    if (cia || voo) partes.push(`${cia} ${voo}`.trim());
    const horario = [dataSaida, saida].filter(Boolean).join(' ');
    if (horario) partes.push(horario);
    return partes.join(' | ');
}

// ── Casador de local (opção × trecho) ────────────────────────────────────────
// Espelho de EventoAereoCasadorLocal no accessapi: o "Vincular órfãs", as cores das células
// e o upload dão a mesma resposta para o mesmo par, e os testes dos dois lados usam a mesma
// tabela de casos. Mudou aqui, muda lá.
//
// Os dois lados viram a mesma descrição antes de comparar: siglas, aeroportos e cidades da
// tabela de metrópoles, e nomes livres. O texto do trecho é quebrado nos separadores, então
// "São Paulo - Guarulhos", "Guarulhos (GRU)" e "GRU" descrevem o mesmo aeroporto. Compara
// palavras inteiras, sem acento: juntar as palavras fazia "BEL" virar pedaço de "belohorizonte".

export const LOCAL_EXATO = 'exato';
// Aeroportos diferentes da mesma metrópole (GRU × CGH): serve de candidato, pinta amarelo.
export const LOCAL_PROXIMO = 'proximo';

// Pontos de cada coisa que bate numa ponta. Mais coisa batendo, mais pontos.
export const PONTO_LOCAL_AEROPORTO = 3;
export const PONTO_LOCAL_SIGLA = 3;
export const PONTO_LOCAL_CIDADE = 2;
export const PONTO_LOCAL_NOME = 2;
// Aeroporto diferente na mesma metrópole (GRU × CGH) é candidato — pinta amarelo e recebe a opção
// se não houver ninguém melhor — mas NÃO pontua. Valendo 1, o trecho que detalhava o aeroporto
// ERRADO ("São Paulo (Congonhas)" para um voo de GRU) passava na frente do genérico ("São Paulo"),
// quando o certo é empatar e o genérico levar: o detalhe não casou com nada.
export const PONTO_LOCAL_PROXIMO = 0;
export const PONTO_LOCAL_MAXIMO = 6;

// Cidades com mais de um aeroporto: sigla da metrópole → nomes da cidade e aeroportos
// (sigla → nomes). É a única ponte entre sigla e nome; cidade fora daqui casa por nome com
// nome ou por sigla com sigla.
export const METRO = {
    SAO: { nomes: ['São Paulo'], aeroportos: { GRU: ['Guarulhos'], CGH: ['Congonhas'], VCP: ['Viracopos', 'Campinas'] } },
    RIO: { nomes: ['Rio de Janeiro'], aeroportos: { GIG: ['Galeão', 'Tom Jobim'], SDU: ['Santos Dumont'] } },
    BHZ: { nomes: ['Belo Horizonte'], aeroportos: { CNF: ['Confins'], PLU: ['Pampulha'] } },
    BUE: { nomes: ['Buenos Aires'], aeroportos: { EZE: ['Ezeiza'], AEP: ['Aeroparque'] } },
    NYC: { nomes: ['Nova York', 'New York'], aeroportos: { JFK: ['Kennedy'], EWR: ['Newark'], LGA: ['LaGuardia'] } },
    LON: { nomes: ['Londres', 'London'], aeroportos: { LHR: ['Heathrow'], LGW: ['Gatwick'], STN: ['Stansted'], LCY: ['London City'] } },
    PAR: { nomes: ['Paris'], aeroportos: { CDG: ['Charles de Gaulle'], ORY: ['Orly'] } },
    MIL: { nomes: ['Milão', 'Milano', 'Milan'], aeroportos: { MXP: ['Malpensa'], LIN: ['Linate'] } },
    ROM: { nomes: ['Roma', 'Rome'], aeroportos: { FCO: ['Fiumicino'], CIA: ['Ciampino'] } },
    TYO: { nomes: ['Tóquio', 'Tokyo'], aeroportos: { NRT: ['Narita'], HND: ['Haneda'] } },
    CHI: { nomes: ['Chicago'], aeroportos: { ORD: ['Hare'], MDW: ['Midway'] } },
    WAS: { nomes: ['Washington'], aeroportos: { IAD: ['Dulles'], DCA: ['Reagan'] } },
};

// Palavras sem acento, em minúscula (o NFD separa a marca do acento, que sai).
const palavrasLocal = (texto) => String(texto ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const contemSequencia = (maior, menor) => menor.length > 0 && menor.length <= maior.length
    && maior.some((_, i) => i + menor.length <= maior.length && menor.every((p, j) => maior[i + j] === p));

// As palavras de um aparecem em sequência no outro.
const casaNomes = (a, b) => contemSequencia(a, b) || contemSequencia(b, a);

const metroDe = (sigla) => (METRO[sigla] ? sigla : (Object.keys(METRO).find(m => METRO[m].aeroportos[sigla]) ?? null));

// Pedaços de nome separados por hífen, barra, vírgula ou parênteses.
const fragmentosLocal = (texto) => String(texto ?? '').split(/[-–—/|,()]+/u).map(palavrasLocal).filter(p => p.length);

const siglaValida = (iata) => {
    const s = String(iata ?? '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(s) ? [s] : [];
};

// Siglas do texto do trecho e o texto sem elas. Tem sigla quando o texto inteiro tem três
// letras ("GRU", "Rio") ou quando há três maiúsculas isoladas por separador ("Rio de Janeiro -
// GIG", "São Paulo (GRU)"). "SAO PAULO" não tem: só espaço não isola a sigla.
function siglasDoTrecho(texto) {
    const s = String(texto ?? '').trim();
    if (/^[A-Za-z]{3}$/.test(s)) return { siglas: [s.toUpperCase()], resto: '' };
    const achadas = [];
    const busca = /[A-Z]{3}/g;
    let m;
    while ((m = busca.exec(s)) !== null) {
        const antes = s.slice(0, m.index).trimEnd();
        const depois = s.slice(m.index + 3).trimStart();
        const isolaAntes = antes === '' || !/[\p{L}\p{N}]$/u.test(antes);
        const isolaDepois = depois === '' || !/^[\p{L}\p{N}]/u.test(depois);
        if (isolaAntes && isolaDepois) achadas.push({ sigla: m[0], posicao: m.index });
    }
    let resto = s;
    [...achadas].reverse().forEach(({ posicao }) => { resto = `${resto.slice(0, posicao)} ${resto.slice(posicao + 3)}`; });
    return { siglas: achadas.map(a => a.sigla), resto };
}

// Descrição de um lado: sigla da tabela vira metrópole ou aeroporto (e a metrópole dele), sigla
// fora da tabela fica "simples". Nome que contém um nome da tabela vira a metrópole ou o
// aeroporto; o que sobra fica como nome livre.
function descreverLocal(siglas, fragmentos) {
    const aeroportos = new Set();
    const metros = new Set();
    const simples = new Set();
    const livres = [];
    siglas.forEach(sigla => {
        const metro = metroDe(sigla);
        if (metro === null) { simples.add(sigla); return; }
        metros.add(metro);
        if (metro !== sigla) aeroportos.add(sigla);
    });
    fragmentos.forEach(fragmento => {
        let resolvido = false;
        Object.entries(METRO).forEach(([metro, dados]) => {
            if (dados.nomes.some(nome => contemSequencia(fragmento, palavrasLocal(nome)))) {
                metros.add(metro);
                resolvido = true;
            }
            Object.entries(dados.aeroportos).forEach(([sigla, nomes]) => {
                if (nomes.some(nome => contemSequencia(fragmento, palavrasLocal(nome)))) {
                    aeroportos.add(sigla);
                    metros.add(metro);
                    resolvido = true;
                }
            });
        });
        if (!resolvido) livres.push(fragmento);
    });
    return { aeroportos, metros, simples, livres };
}

const algumEmComum = (a, b) => [...a].some(x => b.has(x));

/**
 * Uma ponta da opção ({ iata, cidade, aeroporto } de UMA linha) × o texto livre do trecho.
 * Devolve { nivel: LOCAL_EXATO | LOCAL_PROXIMO | null, pontos }.
 */
export function pontuarLocal(partes, textoTrecho) {
    const opcao = descreverLocal(siglaValida(partes?.iata), [...fragmentosLocal(partes?.cidade), ...fragmentosLocal(partes?.aeroporto)]);
    const { siglas, resto } = siglasDoTrecho(textoTrecho);
    const trecho = descreverLocal(siglas, fragmentosLocal(resto));

    let pontos = 0;
    let proximo = false;
    let conflito = false;

    if (opcao.aeroportos.size && trecho.aeroportos.size) {
        if (algumEmComum(opcao.aeroportos, trecho.aeroportos)) pontos += PONTO_LOCAL_AEROPORTO;
        else if (algumEmComum(opcao.metros, trecho.metros)) proximo = true;
        else conflito = true;
    }
    if (opcao.metros.size && trecho.metros.size) {
        if (algumEmComum(opcao.metros, trecho.metros)) pontos += PONTO_LOCAL_CIDADE;
        else conflito = true;
    }
    if (opcao.simples.size && trecho.simples.size) {
        if (algumEmComum(opcao.simples, trecho.simples)) pontos += PONTO_LOCAL_SIGLA;
        else conflito = true;
    }
    // Sigla fora da tabela de um lado e cidade da tabela do outro: lugares diferentes
    // ("BEL" × "Belo Horizonte").
    if ((opcao.simples.size && !opcao.metros.size && trecho.metros.size)
        || (trecho.simples.size && !trecho.metros.size && opcao.metros.size)) {
        conflito = true;
    }
    const nomes = opcao.livres.filter(nome => trecho.livres.some(outro => casaNomes(nome, outro))).length;
    pontos += PONTO_LOCAL_NOME * Math.min(nomes, 2);

    if (conflito || (pontos === 0 && !proximo)) return { nivel: null, pontos: 0 };
    if (proximo) return { nivel: LOCAL_PROXIMO, pontos: PONTO_LOCAL_PROXIMO };
    return { nivel: LOCAL_EXATO, pontos: Math.min(pontos, PONTO_LOCAL_MAXIMO) };
}

// Nível do casamento de uma ponta: LOCAL_EXATO, LOCAL_PROXIMO ou null.
export const casaLocal = (partes, textoTrecho) => pontuarLocal(partes, textoTrecho).nivel;

// Sigla, cidade e aeroporto de uma ponta da opção. Em conexão ('\n'), a origem é a 1ª perna
// e o destino a última — espelha partesDaPonta() do back.
const partesDaPonta = (op, prefixo, linha) => {
    const pegar = (sufixo) => {
        const linhas = String(op?.[prefixo + sufixo] ?? '').split('\n');
        return (linha === 'ultima' ? linhas[linhas.length - 1] : linhas[0]).trim();
    };
    return { iata: pegar('Iata'), cidade: pegar('Cidade'), aeroporto: pegar('Aeroporto') };
};

// ── Relação opção × trecho (validação) ─────────────────────────────────

// Passageiros de um vínculo — trecho, grupo do Aéreo ou origem. Vazio no vínculo vale a quantidade
// da origem dele, a mesma regra do formulário do trecho. `herdada`: só o que ele herda, sem o
// próprio. null quando não há.
export function quantidadeDoVinculo(sid, trechos, { herdada = false } = {}) {
    const s = sid == null ? null : (trechos ?? []).find(x => String(x.id) === String(sid));
    if (!s) return null;
    const vale = (v) => (v === null || v === undefined || v === '' ? null : v);
    const daOrigem = s.origem_id ? (trechos ?? []).find(x => String(x.id) === String(s.origem_id)) : null;
    return (herdada ? null : vale(s.quantidadePassageiros)) ?? vale(s.quantidadeDaOrigem) ?? vale(daOrigem?.quantidadePassageiros) ?? null;
}


// Status de cada opção + resumo por trecho (para badges/cores).
// - opção: 'valida' (relacionada e coerente) | 'divergente' (relacionada, destino difere) | 'orfa' (sem relação).
// - trecho: 'completa' (todas as opções ok) | 'divergente' (alguma opção com pendência) | 'semOpcao' (0 opções).
export function validar(opcoes, trechos) {
    const lista = opcoes ?? [];
    const sols  = trechos ?? [];
    const colunas = verificarColunas(lista, sols);
    const solIds = new Set(sols.map(s => String(s.id)));
    const ehOrfa = (op) => {
        const sid = vinculoDe(op);
        return !sid || !solIds.has(sid);
    };

    // Status por opção (nível de CAMPO, via verificarColunas) — define a COR do chip:
    //   'orfa' (sem trecho) | 'faltando' (≥1 campo faltando) |
    //   'divergente' (≥1 divergente, nenhum faltando) | 'valida' (tudo ok).
    // Bolinha única → prioriza faltando > divergente; a CONTAGEM dos badges abaixo
    // ainda soma nos dois quando a opção tem faltando E divergente.
    const statusPorOpcao = lista.map((op, i) => {
        if (ehOrfa(op)) return 'orfa';
        const campos = Object.values(colunas[i] ?? {});
        if (campos.includes('faltando')) return 'faltando';
        if (campos.includes('divergente')) return 'divergente';
        return 'valida';
    });

    // Contagem OPÇÃO a opção por trecho, no nível de CAMPO: uma opção pode ter
    // campos faltando E divergentes — então soma nos DOIS. Sem nenhum dos dois →
    // completa. Órfã (sem trecho) → fora dos buckets.
    const buckets = {};
    sols.forEach(s => { buckets[String(s.id)] = { completas: 0, divergentes: 0, faltando: 0, total: 0 }; });
    let orfas = 0;
    lista.forEach((op, i) => {
        if (ehOrfa(op)) { orfas++; return; }
        const b = buckets[vinculoDe(op)];
        b.total++;
        const campos = Object.values(colunas[i] ?? {});
        const temFaltando   = campos.includes('faltando');
        const temDivergente = campos.includes('divergente');
        if (temFaltando)   b.faltando++;
        if (temDivergente) b.divergentes++;
        if (!temFaltando && !temDivergente) b.completas++;
    });

    const resumoTrechos = sols.map(s => {
        const b = buckets[String(s.id)];
        // estado "principal" (cor/fallback): completa só com TODAS as opções ok;
        // sem opção alguma → semOpcao (faltando é SÓ campo faltando em opção); senão divergente.
        const estado = b.total === 0 ? 'semOpcao' : (b.completas === b.total ? 'completa' : 'divergente');
        return { trecho: s, estado, ...b };
    });

    // Badges do topo = soma das opções de todas os trechos + órfãs (mesma
    // contagem nível-opção dos buckets, então o topo bate com as linhas).
    const totais = {
        completas:   resumoTrechos.reduce((n, r) => n + r.completas, 0),
        divergentes: resumoTrechos.reduce((n, r) => n + r.divergentes, 0),
        faltando:    resumoTrechos.reduce((n, r) => n + r.faltando, 0),
        orfas,
    };

    return { statusPorOpcao, resumoTrechos, totais };
}

// ── Verificação por COLUNA (célula a célula) ─────────────────────────────────
// Diferente de validar() (status único por opção): aqui cada campo verificável da
// opção é comparado com o trecho relacionada, devolvendo um status por campo
// para colorir SÓ as células verificadas. Campos sem fonte no trecho e opções
// órfãs ficam fora do mapa → célula no estilo padrão (branca).
//   'ok'         (verde)    valor presente e casa com o trecho
//   'divergente' (amarelo)  valor presente, mas não casa
//   'faltando'   (vermelho) valor ausente na opção (o trecho o espera)

// Status de um local (origem/destino) — o mesmo casador do vínculo, contando a sigla.
// Exato → verde; próximo (outro aeroporto da mesma cidade) ou sem casar → amarelo; sigla,
// cidade e aeroporto vazios → vermelho.
// `esperados`: um texto, ou os dois da origem de um trecho preso a uma origem (o dele e o da
// origem pai). Casou exato com algum, verde.
function statusLocal(op, prefixo, esperados, linha) {
    const textos = (Array.isArray(esperados) ? esperados : [esperados]).filter(t => String(t ?? '').trim());
    if (!textos.length) return null; // trecho não tem o valor → não verifica
    const partes = partesDaPonta(op, prefixo, linha);
    if (!partes.iata && !partes.cidade && !partes.aeroporto) return 'faltando';
    return textos.some(t => casaLocal(partes, t) === LOCAL_EXATO) ? 'ok' : 'divergente';
}

// Os textos que uma ponta compara: o lugar e, quando o campo detalha o herdado, o detalhe.
const textosDaPonta = (p) => [p?.base, p?.detalhe];

// Extrai dia/mês/ano de "DD/MM/AAAA" (trecho e tabela), "DD/MM" (sem ano) ou
// "YYYY-MM-DD" (banco). Pega a 1ª linha quando há itinerário multi-trecho.
function parseData(v) {
    const s = String(v ?? '').split('\n')[0].trim();
    let m = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return { y: m[1], mo: m[2].padStart(2, '0'), d: m[3].padStart(2, '0') };
    m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m) return { y: m[3].length === 2 ? '20' + m[3] : m[3], mo: m[2].padStart(2, '0'), d: m[1].padStart(2, '0') };
    m = s.match(/(\d{1,2})\/(\d{1,2})/);
    if (m) return { y: null, mo: m[2].padStart(2, '0'), d: m[1].padStart(2, '0') };
    return null;
}

// Datas casam por dia + mês (e ano, quando ambas o têm) — tolera a tabela sem ano.
function datasCasam(a, b) {
    const pa = parseData(a), pb = parseData(b);
    if (!pa || !pb) return false;
    if (pa.d !== pb.d || pa.mo !== pb.mo) return false;
    return (!pa.y || !pb.y) ? true : pa.y === pb.y;
}

// Linha relevante de um campo multi-trecho (conexões separadas por '\n').
const primeiraLinhaDe = (v) => String(v ?? '').split('\n')[0];
const ultimaLinhaDe = (v) => {
    const l = String(v ?? '').split('\n');
    return l[l.length - 1];
};

/**
 * Espelha statusData para as horas. O horário do trecho é uma JANELA de/até (um lado só é o
 * limite correspondente; até < de vira a meia-noite — `pontosNaJanela`). Dentro dela, verde;
 * fora, amarelo, para o operador olhar. Nunca vira erro duro — a hora pontua, não desclassifica.
 */
function statusHora(valor, de, ate) {
    if (!String(de ?? '').trim() && !String(ate ?? '').trim()) return null; // trecho sem hora → não verifica
    if (!String(valor ?? '').trim()) return 'faltando';
    return pontosNaJanela(valor, de, ate) === PONTO_HORA_EXATA ? 'ok' : 'divergente';
}

function statusData(valor, esperado) {
    if (!String(esperado ?? '').trim()) return null; // trecho sem data → não verifica
    if (!String(valor ?? '').trim()) return 'faltando';
    return datasCasam(valor, esperado) ? 'ok' : 'divergente';
}

// Mapa campo→status por opção (paralelo a `opcoes`). Verifica rota, data e horas de
// cada direção que o trecho PEDE; o retorno espelha a rota (retOrigem↔destino,
// retDestino↔origem). A direção que o trecho não pede, se vier na opção, é divergência:
// é voo cotado que ninguém pediu.
export function verificarColunas(opcoes, trechos) {
    const sols = trechos ?? [];
    return (opcoes ?? []).map(op => {
        const sid = vinculoDe(op);
        const s = sid ? sols.find(x => String(x.id) === sid) : null;
        if (!s) return {}; // órfã → nada verificado (célula padrão)
        const mapa = {};
        const set = (campo, status) => { if (status) mapa[campo] = status; };
        // Horas entram na verificação porque entram no pareamento — pintar o que
        // pontua, e só isso. Em conexão, saída da primeira perna e chegada da última.
        const p = pontasDe(s);
        if (pedeIda(s)) {
            set('origemIda',  statusLocal(op, 'idaOrigem',  textosDaPonta(p.origemIda), 'primeira'));
            set('destinoIda', statusLocal(op, 'idaDestino', textosDaPonta(p.destinoIda), 'ultima'));
            set('dataIda',    statusData(op.idaData, dataDoVinculo(s, 'ida')));
            set('saidaIda',   statusHora(primeiraLinhaDe(op.idaSaida), s.horaSaidaIdaDe, s.horaSaidaIdaAte));
            set('chegadaIda', statusHora(ultimaLinhaDe(op.idaChegada), s.horaChegadaIdaDe, s.horaChegadaIdaAte));
        } else if (opcaoTemLado(op, 'ida')) {
            ['origemIda', 'destinoIda', 'dataIda', 'saidaIda', 'chegadaIda'].forEach(c => set(c, 'divergente'));
        }
        if (pedeRetorno(s)) {
            set('origemRet',  statusLocal(op, 'retOrigem',  textosDaPonta(p.origemVolta), 'primeira'));
            set('destinoRet', statusLocal(op, 'retDestino', textosDaPonta(p.destinoVolta), 'ultima'));
            set('dataRet',    statusData(op.retData, dataDoVinculo(s, 'ret')));
            set('saidaRet',   statusHora(primeiraLinhaDe(op.retSaida), s.horaSaidaRetornoDe, s.horaSaidaRetornoAte));
            set('chegadaRet', statusHora(ultimaLinhaDe(op.retChegada), s.horaChegadaRetornoDe, s.horaChegadaRetornoAte));
        } else if (opcaoTemLado(op, 'ret')) {
            ['origemRet', 'destinoRet', 'dataRet', 'saidaRet', 'chegadaRet'].forEach(c => set(c, 'divergente'));
        }
        return mapa;
    });
}

// ── Vínculo automático das opções órfãs ──────────────────────────────────────
// Mesmo ranking do upload (relacionarPorRota/melhorVinculo no back), sobre a mesma lista de
// vínculos: trechos com a sobrescrita aplicada e grupos avulsos, sem precedência entre eles.
// A editada é o próprio trecho corrigido; deixá-la para depois do original fazia a correção
// perder para o texto errado.

// "14:30" / "14:30:00" → minutos desde a meia-noite; null se não parseia.
// Declaração de função (não const): `statusHora` está ACIMA daqui no arquivo e
// chama os pontos de hora — com arrow em const isso dependeria da ordem de avaliação.
function minutosDe(v) {
    const m = String(v ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const h = Number(m[1]), min = Number(m[2]);
    return (h > 23 || min > 59) ? null : h * 60 + min;
}

// Pesos do pareamento. A DATA domina: opções do mesmo trecho compartilham a data, então
// data diferente é outro trecho. Tudo o que não é data soma no máximo 32 — as pontas de ida
// e de volta (até PONTO_LOCAL_MAXIMO cada) e as quatro horas (até 2 cada) —, e a data vale
// 50: local e horas desempatam DENTRO da mesma data sem nunca virar a decisão contra ela.
// Espelhado no accessapi (EventoAereoMalhaOpcaoController) — os dois têm de andar juntos.
export const PONTO_DATA = 50;
export const PONTO_HORA_EXATA = 2;
export const PONTO_HORA_PROXIMA = 1;
export const TOLERANCIA_HORA_MIN = 60;

// Cada horário do trecho (saída, chegada) é uma JANELA de/até — ver `pontosNaJanela`. Com um só
// lado preenchido ela degenera nestes dois limites: só "de" é "a partir de", só "até" é "até".
// A hora da opção que respeita o limite pontua cheio; a que passa dele por até uma hora pontua
// pouco (a agência cota 13:50 para quem pediu a partir de 14:00); o resto não pontua. Hora nunca
// desclassifica: trecho sem hora preenchida (o caso comum) não pode orfanar a opção.
// Espelhado no accessapi (EventoAereoMalhaOpcaoController).
export function pontosHoraAPartirDe(horaOpcao, limite) {
    const a = minutosDe(horaOpcao), b = minutosDe(limite);
    if (a === null || b === null) return 0;
    if (a >= b) return PONTO_HORA_EXATA;
    return b - a <= TOLERANCIA_HORA_MIN ? PONTO_HORA_PROXIMA : 0;
}

export function pontosHoraAte(horaOpcao, limite) {
    const a = minutosDe(horaOpcao), b = minutosDe(limite);
    if (a === null || b === null) return 0;
    if (a <= b) return PONTO_HORA_EXATA;
    return a - b <= TOLERANCIA_HORA_MIN ? PONTO_HORA_PROXIMA : 0;
}

/**
 * Pontos de uma hora da opção contra a JANELA de/até do trecho.
 *
 * Os dois lados preenchidos são um intervalo, e ele é CIRCULAR: "até" menor que "de" é a janela
 * virando a meia-noite — 23:00 até 02:00 cobre 23, 0, 1 e 2. Não é erro de digitação, é o voo da
 * madrugada, e lido como intervalo vazio ele nunca pontuaria. Dentro da janela, cheio; fora por
 * até uma hora de qualquer borda (também na volta do relógio), pouco; o resto, nada.
 *
 * Um lado só é o limite de antes (`pontosHoraAPartirDe` / `pontosHoraAte`); nenhum lado é
 * "sem pedido", e não pontua nem desclassifica. Espelhado no accessapi.
 */
export function pontosNaJanela(horaOpcao, de, ate) {
    const temDe = minutosDe(de) !== null, temAte = minutosDe(ate) !== null;
    if (!temDe && !temAte) return 0;
    if (!temAte) return pontosHoraAPartirDe(horaOpcao, de);
    if (!temDe) return pontosHoraAte(horaOpcao, ate);
    const h = minutosDe(horaOpcao);
    if (h === null) return 0;
    const a = minutosDe(de), b = minutosDe(ate);
    const dentro = a <= b ? (h >= a && h <= b) : (h >= a || h <= b);
    if (dentro) return PONTO_HORA_EXATA;
    // Distância, no relógio, até a borda mais perto: quanto falta para a janela abrir e quanto
    // passou de ela fechar. O módulo é o que faz 22:30 contar como "meia hora antes das 23".
    const DIA = 24 * 60;
    const antes = (a - h + DIA) % DIA;
    const depois = (h - b + DIA) % DIA;
    return Math.min(antes, depois) <= TOLERANCIA_HORA_MIN ? PONTO_HORA_PROXIMA : 0;
}

/**
 * Pontuação de uma opção contra um trecho, depois de a rota já ter filtrado. Data e
 * horas (saída a partir de, chegada até) de cada direção que o trecho PEDE; a que ele não
 * pede não pontua. Em conexão ('\n' multi-trecho) a saída é a da primeira perna e a
 * chegada é a da última — mesma regra que origem/destino já usam. Espelha
 * pontosDirecoes() do back.
 */
export const pontuarTrecho = (op, s) => {
    const linhas = (v) => String(v ?? '').split('\n');
    const ultima = (v) => linhas(v)[linhas(v).length - 1];
    let pontos = 0;
    // Saída e chegada são JANELAS (de/até), cada uma valendo até 2: a saída da opção contra a
    // janela de saída, a chegada contra a de chegada. Antes só o "de" da saída e o "até" da
    // chegada eram lidos — os outros dois campos da tela não contavam para nada.
    if (pedeIda(s)) {
        pontos += (datasCasam(op.idaData, dataDoVinculo(s, 'ida')) ? PONTO_DATA : 0)
            + pontosNaJanela(linhas(op.idaSaida)[0], s.horaSaidaIdaDe, s.horaSaidaIdaAte)
            + pontosNaJanela(ultima(op.idaChegada), s.horaChegadaIdaDe, s.horaChegadaIdaAte);
    }
    if (pedeRetorno(s)) {
        pontos += (datasCasam(op.retData, dataDoVinculo(s, 'ret')) ? PONTO_DATA : 0)
            + pontosNaJanela(linhas(op.retSaida)[0], s.horaSaidaRetornoDe, s.horaSaidaRetornoAte)
            + pontosNaJanela(ultima(op.retChegada), s.horaChegadaRetornoDe, s.horaChegadaRetornoAte);
    }
    return pontos;
};

/**
 * Vínculo da opção pelo ranking único — espelha melhorVinculo() do back.
 *
 * A rota da opção decide a direção: com ida, pela ida, e só em vínculo que pede ida; só com
 * retorno, pela rota espelhada (a volta parte do destino e chega na origem). O candidato normal
 * casa origem E destino, ao menos como próximo. Só quando nenhum vínculo casa as duas entra o
 * último recurso: o vínculo que casa só a cidade do passageiro (a origem da ida, o destino da
 * volta), ou só a outra ponta quando ela é própria do trecho ou do grupo (não herdada do pedido)
 * e a opção não chega por ela à cidade do evento, porque vincular por elas é melhor do que deixar
 * a opção órfã. Os pontos somam tudo o
 * que bate: as pontas (mais campo batendo, mais ponto; a que não casou vale zero), a volta da
 * opção contra a volta pedida, datas e horas das direções pedidas. Maior soma vence; empate no
 * topo → órfã (null). Candidato único vence mesmo sem data: a cor da célula mostra a divergência.
 */
/**
 * As pontas de um vínculo, prontas para comparar. Hoje o back NÃO manda `pontas` no vínculo
 * (`EventoMalhaAerea::pontas()` só é chamada pelo casador do upload); a conta é sempre feita aqui,
 * pela mesma regra, a partir dos valores resolvidos e das referências `origemPai`/`destinoPedido`.
 * O `s.pontas` fica como atalho para o dia em que o back passar a mandar.
 */
export const pontasDe = (s) => (s?.pontas
    ? s.pontas
    // Sem `destinoPedido` a referência é VAZIA, nunca o próprio destino: o vínculo chega do back
    // com o destino já resolvido, e usá-lo como referência de si mesmo compunha "Salvador
    // (Salvador)". O back só manda `destinoPedido` quando o trecho tem destino próprio — é a
    // mesma regra do `origemPai`.
    : pontasDoVinculo(s, { origemPai: s?.origemPai, destino: s?.destinoPedido ?? '' }));

/**
 * Uma ponta da opção contra uma ponta do vínculo. Quando o campo detalha o herdado, os dois
 * textos pontuam: os pontos somam e vale o melhor nível — uma opção de Congonhas casa melhor com
 * "São Paulo (Congonhas)" do que uma de Guarulhos. Espelha pontuarPonta() do back.
 */
export function pontuarPonta(partes, p) {
    const base = pontuarLocal(partes, p?.base);
    if (!String(p?.detalhe ?? '').trim()) return base;
    const detalhe = pontuarLocal(partes, p.detalhe);
    const nivel = [LOCAL_EXATO, LOCAL_PROXIMO].find(n => base.nivel === n || detalhe.nivel === n) ?? null;
    return { nivel, pontos: base.pontos + detalhe.pontos };
}

/**
 * A ponta tem lugar PRÓPRIO: texto no campo e, com o badge do herdado ligado, um detalhe sobre o
 * herdado. Sem texto, ou com o badge ligado e nada além do herdado, o lugar é o de cima. `ponta` é
 * a resolvida por `pontasDe`; `campo` e `herdado` são o texto e o badge dela no vínculo. Espelha
 * `EventoMalhaAerea::pontaPropria()` no back.
 */
function pontaPropria(s, ponta, campo, herdado) {
    const texto = (v) => String(v ?? '').trim();
    return texto(s?.[campo]) !== '' && (!s?.[herdado] || texto(ponta?.detalhe) !== '');
}

/**
 * Quanto lugar PRÓPRIO um vínculo tem: zero é o mais genérico. Conta as pontas em que o texto
 * não é só o herdado — detalhe sobre o herdado, badge desligado com texto, ou volta com lugar
 * próprio. É o critério do desempate: entre iguais em pontos, vale o mais genérico. Espelha
 * `EventoMalhaAerea::especificidade()` no back.
 */
export function especificidadeDe(s) {
    const p = pontasDe(s);
    return [
        pontaPropria(s, p.origemIda, 'origem', 'origemHerdada'),
        pontaPropria(s, p.destinoIda, 'destino', 'destinoHerdado'),
        pontaPropria(s, p.origemVolta, 'origemVolta', 'origemVoltaHerdada'),
        pontaPropria(s, p.destinoVolta, 'destinoVolta', 'destinoVoltaHerdado'),
    ].filter(Boolean).length;
}

export function melhorVinculo(op, vinculos) {
    const temIda = opcaoTemLado(op, 'ida');
    const temRet = opcaoTemLado(op, 'ret');
    if (!temIda && !temRet) return null;
    const partes = (lado) => ({
        origem: partesDaPonta(op, `${lado}Origem`, 'primeira'),
        destino: partesDaPonta(op, `${lado}Destino`, 'ultima'),
    });

    const completos = [];
    const parciais = [];
    (vinculos ?? []).forEach(s => {
        // A direção que decide é a que o vínculo PEDE e a cotação TEM. "Sem trecho" numa direção
        // não filtra a cotação — só tira aquela direção da conta: um trecho só de volta casa uma
        // cotação de ida e volta pela volta dela, ignorando a ida que ninguém pediu. Cotação sem
        // nenhuma direção que o vínculo peça não é candidata.
        const usaIda = pedeIda(s) && temIda;
        const usaRet = pedeRetorno(s) && temRet;
        if (!usaIda && !usaRet) return;
        // As quatro pontas já vêm resolvidas pelas flags (herdado, da volta): a volta não é mais
        // o espelho presumido da ida — é a volta que a solicitação pediu. Cada texto — o herdado
        // e o que o detalha — pontua em separado contra os três campos da cotação (`pontuarPonta`).
        const p = pontasDe(s);
        const { origem, destino } = partes(usaIda ? 'ida' : 'ret');
        const casaOrigem = pontuarPonta(origem, usaIda ? p.origemIda : p.origemVolta);
        const casaDestino = pontuarPonta(destino, usaIda ? p.destinoIda : p.destinoVolta);
        // O parcial vale pela ponta que é a cidade do passageiro: a origem da ida, ou o destino da
        // volta (ela volta para a origem). A outra ponta — o destino da ida, a origem da volta — só
        // vale quando é PRÓPRIA do trecho ou do grupo (nunca da origem): herdada, ela é a cidade do
        // evento, comum a todos os vínculos, e casar só por ela levava uma cidade que não está na
        // malha para o vínculo que somasse um ponto a mais de data ou horário. Própria, ela
        // identifica aquele vínculo, como o grupo que vai para Salvador num evento em São Paulo.
        // Própria pelo detalhe, ela ainda pontua pelo herdado ("São Paulo (Salvador)" casa São
        // Paulo): a opção que chega à cidade do evento não vale por essa ponta, nem com o detalhe
        // de aeroporto ("São Paulo (Guarulhos)" continua sendo a cidade do evento).
        const casaCidade = (usaIda ? casaOrigem : casaDestino).nivel;
        const casaOutra = (usaIda ? casaDestino : casaOrigem).nivel;
        const outraPropria = baseDoVinculo(s) !== 'origem' && (usaIda
            ? pontaPropria(s, p.destinoIda, 'destino', 'destinoHerdado')
            : pontaPropria(s, p.origemVolta, 'origemVolta', 'origemVoltaHerdada'));
        const casaEvento = casaLocal(usaIda ? destino : origem, s?.destinoPedido ?? '');
        if (!casaCidade && !(casaOutra && outraPropria && !casaEvento)) return;
        // A ponta que não casou pontua zero: no parcial, a soma é só a da que casou.
        let pontos = casaOrigem.pontos + casaDestino.pontos + pontuarTrecho(op, s);
        // As duas direções pedidas E cotadas: a volta também soma contra a volta pedida, sem filtrar.
        if (usaIda && usaRet) {
            const ret = partes('ret');
            pontos += pontuarPonta(ret.origem, p.origemVolta).pontos + pontuarPonta(ret.destino, p.destinoVolta).pontos;
        }
        (casaOrigem.nivel && casaDestino.nivel ? completos : parciais).push({ s, pontos });
    });
    // Último recurso: sem nenhum vínculo que case as duas pontas, concorre quem casa uma só.
    // Havendo um completo, o parcial nem entra na conta, por mais que some.
    const candidatos = completos.length ? completos : parciais;
    if (!candidatos.length) return null;
    candidatos.sort((a, b) => b.pontos - a.pontos);
    const [primeiro, segundo] = candidatos;
    if (!segundo || segundo.pontos !== primeiro.pontos) return primeiro.s;
    return desempatar(candidatos.filter(c => c.pontos === primeiro.pontos));
}

// Empate no topo: vale o MAIS GENÉRICO — quem tem menos lugar próprio (`especificidadeDe`). Se
// dois somam o mesmo, o detalhe do mais específico não casou com nada (senão ele estaria na
// frente), e creditar a ele o que só o genérico explica seria adivinhar. Sobrando mais de um
// igualmente genérico, resta a exceção da origem × o trecho neutro dela: vale o trecho, porque a
// trava de duplicidade garante que ele é único, e a origem segue no painel de qualquer forma.
// Qualquer outro empate vira órfã — a opção fica visível para alguém arrastar.
function desempatar(empatados) {
    const menor = Math.min(...empatados.map(c => especificidadeDe(c.s)));
    const genericos = empatados.filter(c => especificidadeDe(c.s) === menor);
    if (genericos.length === 1) return genericos[0].s;
    const neutro = genericos.length === 2 ? genericos.find(c => ehTrechoNeutro(c.s)) : null;
    const outro = neutro ? genericos.find(c => c !== neutro) : null;
    const paiDoNeutro = String(neutro?.s?.origem_id ?? '');
    if (paiDoNeutro && outro && baseDoVinculo(outro.s) === 'origem' && String(outro.s.id) === paiDoNeutro) {
        return neutro.s;
    }
    return null;
}

/**
 * "Vincular órfãs" — o mesmo vínculo por rota do upload (relacionarPorRota() do back), sobre os
 * vínculos da TELA: trechos, trechos editados e grupos, inclusive os ainda não salvos.
 */
export function vincularOrfas(opcoes, trechos) {
    const sols = trechos ?? [];
    // Mesmas chaves do back: o id do vínculo e, no trecho editado, o id da sobrescrita. Grupo
    // criado na tela ("novo-...") já está na lista com o próprio id.
    const solIds = new Set(sols.flatMap(s => [s.id, s.grupo]).filter(v => v != null && v !== '').map(String));
    let vinculadas = 0;

    const novas = (opcoes ?? []).map(op => {
        // Já relacionada → preserva (mesma guarda do backend): correção manual
        // feita no board tem precedência sobre o palpite automático. É ESTA linha
        // que protege o trabalho manual — não o tipo do vínculo.
        const sid = vinculoDe(op);
        if (sid && solIds.has(sid)) return op;

        const melhor = melhorVinculo(op, sols);
        // Vínculo pendurado (trecho, grupo ou origem que não existe mais, ou origem que ganhou
        // trecho) sem casamento sai, como no upload: a opção fica órfã em vez de apontar para
        // id morto.
        if (!melhor) return sid ? comVinculo(op, null) : op;
        vinculadas++;
        // Grava a coluna do que o vínculo é por baixo da edição, e zera as outras duas.
        return comVinculo(op, melhor);
    });

    return { opcoes: novas, vinculadas };
}

// ── Sugestão de voos: o grupo da cidade ──────────────────────────────────────
// A opção tem ida pelas pontas dela (sigla, cidade ou aeroporto de origem e de destino), como o `temAlgumaPonta` do
// back: data, cia e voo sozinhos não fazem a ida.
const PONTAS_DA_IDA = ['idaOrigemIata', 'idaOrigemCidade', 'idaOrigemAeroporto', 'idaDestinoIata', 'idaDestinoCidade', 'idaDestinoAeroporto'];
const temPontaDaIda = (op) => PONTAS_DA_IDA.some(k => String(op?.[k] ?? '').trim() !== '');
// A cidade do passageiro numa opção, como o upload da sugestão agrupa (`agruparPorCidade` do back): a origem da ida
// e, na opção só de volta, o destino dela, a cidade para onde ele volta. A cidade e, sem ela, a sigla.
export const cidadeDoPassageiro = (op) => {
    const linhas = (v) => String(v ?? '').split('\n').map(l => l.trim());
    const ultima = (v) => { const l = linhas(v); return l[l.length - 1] || ''; };
    if (temPontaDaIda(op)) return linhas(op.idaOrigemCidade)[0] || linhas(op.idaOrigemIata)[0] || '';
    return ultima(op?.retDestinoCidade) || ultima(op?.retDestinoIata) || '';
};
// O destino do passageiro numa opção, o par da `cidadeDoPassageiro`: o fim da ida e, na opção só de volta, de onde ela
// sai. A cidade e, sem ela, a sigla.
export const destinoDoPassageiro = (op) => {
    const linhas = (v) => String(v ?? '').split('\n').map(l => l.trim());
    const ultima = (v) => { const l = linhas(v); return l[l.length - 1] || ''; };
    if (temPontaDaIda(op)) return ultima(op.idaDestinoCidade) || ultima(op.idaDestinoIata) || '';
    return linhas(op?.retOrigemCidade)[0] || linhas(op?.retOrigemIata)[0] || '';
};
// A chave da cidade para comparar, como o `normalizarLocal` do back: sem acento, sem maiúscula, só letra e número.
export const chaveCidade = (t) => String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * O "Vincular órfãs" e a Nova linha da sugestão de voos: cada órfã vai para o grupo da cidade do passageiro e do
 * destino dele, e o par sem grupo ganha um, só com a origem e o destino, com o id de `novoId()`, que o Salvar troca
 * pelo real. A opção que já está num vínculo da tela fica nele. Devolve { opcoes, novos (os grupos criados),
 * vinculadas }.
 */
export function vincularPorCidade(opcoes, trechos, novoId) {
    const lista = trechos ?? [];
    const ids = new Set(lista.flatMap(s => [s.id, s.grupo]).filter(v => v != null && v !== '').map(String));
    const chave = (origem, destino) => `${chaveCidade(origem)}|${chaveCidade(destino)}`;
    const porCidade = new Map();
    lista.filter(s => baseDoVinculo(s) === 'grupo').forEach(s => {
        const k = chave(s.origem, s.destino);
        if (chaveCidade(s.origem) && !porCidade.has(k)) porCidade.set(k, s);
    });
    const novos = [];
    let vinculadas = 0;
    const novas = (opcoes ?? []).map(op => {
        const sid = vinculoDe(op);
        if (sid && ids.has(sid)) return op;
        const cidade = cidadeDoPassageiro(op);
        if (!chaveCidade(cidade)) return op;
        const destino = destinoDoPassageiro(op);
        const k = chave(cidade, destino);
        let grupo = porCidade.get(k);
        if (!grupo) {
            const id = novoId();
            grupo = { id, grupo: id, tipo: 'grupo', base: 'grupo', origem: cidade, destino: destino || null, ida: true, retorno: true, ordem: lista.length + novos.length };
            porCidade.set(k, grupo);
            novos.push(grupo);
        }
        vinculadas++;
        return comVinculo(op, grupo);
    });
    return { opcoes: novas, novos, vinculadas };
}

// ── Agrupar trechos do MESMO lado (ida OU ret) num itinerário multi-linha ────
// Operação por LADO (não por opção inteira): o modal Ajustar agrupa dentro de
// cada coluna. TODAS as colunas do lado (texto E moeda) empilham com '\n' — cada
// trecho numa linha. O `total` continua único (grand sum via calcularTotal, que
// soma todas as linhas). Usado no Aplicar (mesclarLado) e na abertura (separarLado).

const keysLado = (lado) => (lado === 'ret' ? RET_KEYS : IDA_KEYS);

// Reclassifica um trecho de um lado para o outro (ida↔ret) renomeando os campos
// POSICIONALMENTE — IDA_KEYS[i] → RET_KEYS[i] (mesma origem→destino, sem swap). Usado
// ao arrastar um trecho entre as colunas IDA e RETORNO no modal Ajustar: o voo é o
// mesmo, só muda em qual coluna ele vive. Preserva _id/_uid/_meta.
export function remapLado(trecho, ladoDe, ladoPara) {
    if (!trecho || ladoDe === ladoPara) return trecho;
    const de = keysLado(ladoDe), para = keysLado(ladoPara);
    const out = { ...trecho };
    de.forEach((k, i) => { out[para[i]] = trecho[k] ?? ''; delete out[k]; });
    return out;
}

// Divide um trecho de um lado em N trechos de linha única (inverso de mesclarLado).
// N = máx. de linhas entre TODOS os campos (texto e moeda); campo sem a linha i
// recebe ''. Round-trip EXATO com mesclarLado. O 1º herda _id/_meta do original
// (fundir lê _meta do lado p/ numParticipantes/tkt/obs).
export function separarLado(item, lado) {
    if (!item) return [];
    const keys = keysLado(lado);
    const linhasDe = (k) => String(item[k] ?? '').split('\n');
    const n = Math.max(1, ...keys.map(k => linhasDe(k).length));
    const trechos = [];
    for (let i = 0; i < n; i++) {
        const t = {};
        keys.forEach(k => { t[k] = linhasDe(k)[i] ?? ''; });
        t._id = i === 0 ? item._id : `${item._id}#${i}`;
        if (i === 0) t._meta = item._meta;
        trechos.push(t);
    }
    return trechos;
}

// ── Merge por VALOR de uma coluna (rowspan "vivo" na tabela) ─────────────────
// Diferente do rowspan cego antigo: linhas de um grupo só se juntam quando o valor
// casa. Célula vazia adota o valor comum; valor divergente fica sozinho.

// Valor comum de uma coluna num grupo: o não-vazio mais frequente (empate → 1º).
// '' se todos vazios. `prioridade`: sem um mais frequente (empate), vale ela quando
// preenchida — é o valor do grupo, o modal, que decide o empate.
export function valorComum(valores, prioridade = null) {
    const cont = new Map();
    (valores ?? []).forEach(v => {
        const s = String(v ?? '');
        if (s !== '') cont.set(s, (cont.get(s) || 0) + 1);
    });
    let melhor = '', max = 0, empate = false;
    cont.forEach((n, v) => {
        if (n > max) { max = n; melhor = v; empate = false; } else if (n === max) empate = true;
    });
    const p = String(prioridade ?? '');
    return empate && p !== '' ? p : melhor;
}

// Mescla uma coluna de um grupo. Vazio vira o valor comum; runs contíguos de valor
// igual viram um rowspan. Devolve array alinhado às linhas: null = coberta pelo rowspan
// anterior, senão { span, value, targets } — targets = índices (no grupo) de TODAS as
// linhas com esse valor efetivo, p/ a edição propagar mesmo entre runs não-contíguos.
// `extras`: linhas que votam no comum sem aparecer na tabela, no fim da lista (ver `extrasDaMescla`);
// no empate, vale `prioridade` — por padrão, a delas.
// `cobre`: as linhas do run, as que o rowspan cobre — é a elas que o destaque da célula vai.
export function mesclarColuna(valores, extras = [], prioridade = extras[0]) {
    const comum = valorComum([...(valores ?? []), ...extras], prioridade);
    const efetivo = (valores ?? []).map(v => String(v ?? '') === '' ? comum : String(v ?? ''));
    const out = efetivo.map(() => null);
    for (let i = 0; i < efetivo.length; ) {
        let j = i + 1;
        while (j < efetivo.length && efetivo[j] === efetivo[i]) j++;
        const targets = efetivo.reduce((acc, v, k) => v === efetivo[i] ? [...acc, k] : acc, []);
        out[i] = { span: j - i, value: efetivo[i], targets, cobre: Array.from({ length: j - i }, (_, k) => i + k) };
        i = j;
    }
    return out;
}

/**
 * O valor do MODAL do vínculo numa coluna mesclada. É MAIS UMA LINHA da coluna: passa pelas mesmas
 * regras da mescla, como a última linha do grupo. Duas colunas têm essa linha:
 *   - OBS: a observação do trecho, da edição dele ou do grupo do Aéreo. A origem não entra: é o
 *     pedido do RSVP e não tem modal.
 *   - Num. Part.: os passageiros do vínculo, com a herança da origem (`quantidadeDoVinculo`). A
 *     origem entra só para votar: é o número do pedido, e não se edita por aqui.
 * null quando o vínculo não tem essa linha. `herdada`: sem o valor próprio — o que sobra do modal
 * quando ele é esvaziado.
 */
export function valorDoModal(sid, campo, trechos, { herdada = false } = {}) {
    const s = sid == null ? null : (trechos ?? []).find(x => String(x.id) === String(sid));
    if (!s) return null;
    // A origem também: a observação dela se edita como a do trecho, e vazia vale a do pedido.
    if (campo === 'obs') return herdada ? '' : String(s.observacao ?? '');
    if (campo === 'numParticipantes') return String(quantidadeDoVinculo(sid, trechos, { herdada }) ?? '');
    return null;
}

// A linha do modal no voto do comum (`mesclarColuna`, `normalizarMerge`).
export function extrasDaMescla(sid, campo, trechos) {
    const valor = valorDoModal(sid, campo, trechos);
    return valor ? [valor] : [];
}

// As linhas no run do modal: ele entra no fim da lista e passa pelo mesmo `mesclarColuna`, com a
// prioridade no empate. É quem edita junto com ele. Índices na lista dada.
export function linhasNoRunDoModal(valores, doModal) {
    const lista = [...(valores ?? []), doModal ?? ''];
    const ultimo = lista.length - 1;
    return mesclarColuna(lista, [], doModal).find(r => r && r.targets.includes(ultimo)).targets.filter(k => k !== ultimo);
}

// Ao Salvar: em cada grupo de trecho, preenche as células vazias dos `campos`
// com o valor comum do grupo (persiste o auto-preenchimento do merge). `extrasDe(sid, campo)`:
// as linhas que não aparecem (ver `extrasDaMescla`) — o que se grava é o que a tabela mostra.
export function normalizarMerge(opcoes, campos, extrasDe = null) {
    const lista = (opcoes ?? []).map(op => ({ ...op }));
    const grupos = new Map();
    lista.forEach((op, i) => {
        const sid = vinculoDe(op);
        if (!sid) return;
        if (!grupos.has(sid)) grupos.set(sid, []);
        grupos.get(sid).push(i);
    });
    grupos.forEach((idxs, sid) => {
        (campos ?? []).forEach(campo => {
            const extras = extrasDe ? extrasDe(sid, campo) : [];
            const comum = valorComum([...idxs.map(i => lista[i][campo]), ...extras], extras[0]);
            if (comum === '') return;
            idxs.forEach(i => { if (String(lista[i][campo] ?? '') === '') lista[i][campo] = comum; });
        });
    });
    return lista;
}

// Funde os trechos de um lado num só: TODA coluna (texto e moeda) empilha com '\n'.
// Preserva _id/_meta do 1º. NÃO calcula `total` (é do par ida+ret — fundir faz).
export function mesclarLado(itens, lado) {
    const lista = (itens ?? []).filter(Boolean);
    if (!lista.length) return null;
    const base = { ...lista[0] };
    keysLado(lado).forEach(k => { base[k] = lista.map(t => String(t[k] ?? '')).join('\n'); });
    return base;
}

// Texto e cor de cada situação da MALHA. Os valores vêm do back
// (`EventoMalhaAerea::situacoes()`); o rótulo acentuado e a cor são de tela.
//
// Moram aqui, e não numa das telas, porque o painel de Malhas e a Importação mostram
// a mesma pill — duas cópias divergiriam e o mesmo estado apareceria com nomes
// diferentes conforme a tela.
// `alteracao` é o pedido do RSVP esperando o Aéreo; `alterada`, o reenvio do Aéreo esperando o
// RSVP. "Em alteração" e "Alterada" para as duas não se confundirem lado a lado.
export const ROTULO_SITUACAO = {
    pendente:  'Rascunho',
    enviada:   'Aguardando aprovação',
    aprovada:  'Aprovada',
    reprovada: 'Reprovada',
    alteracao: 'Em alteração',
    alterada:  'Alterada',
    // Nacional enviada: sem aprovação, a malha está com o RSVP para importar; com opção no
    // evento, importada.
    liberada:  'Aguardando RSVP',
    importada: 'Importada',
};

// `alteracao` fica no laranja do warning: é o estado que pede ação de alguém.
// `enviada` cede o laranja e vai para o azul — esperar aprovação é informação, não
// alerta. O `.fin-pp` só tem cinco variantes de pill e nenhuma outra quente, então as
// duas no warning ficariam indistinguíveis, que é o mesmo que não ter status. `alterada`
// também espera decisão, e fica no azul da `enviada`.
export const COR_SITUACAO = {
    pendente:  'is-muted',
    enviada:   'is-info',
    aprovada:  'is-ok',
    reprovada: 'is-danger',
    alteracao: 'is-warn',
    alterada:  'is-info',
    liberada:  'is-info',
    importada: 'is-ok',
};

// Situação da SOLICITAÇÃO (pedido). Valores de `EventoAereoSolicitacao::situacoes()`;
// rótulo e cor são de tela. Mesma lógica de cor da malha: rascunho apagado, alteração
// no laranja de quem precisa agir, conclusão no verde.
export const ROTULO_SITUACAO_SOLICITACAO = {
    pendente:   'Rascunho',
    solicitada: 'Solicitada',
    alteracao:  'Alteração',
    concluida:  'Concluída',
    cancelada:  'Cancelada',
};

export const COR_SITUACAO_SOLICITACAO = {
    pendente:   'is-muted',
    solicitada: 'is-info',
    alteracao:  'is-warn',
    concluida:  'is-ok',
    cancelada:  'is-danger',
};

// Grupos de perfil do fluxo de aéreo — espelho de `EventoAereoAprovacaoService`. Servem só
// para mostrar ou esconder controle; quem decide é o back, que recusa de todo jeito. O RSVP é
// o time todo menos a Lista Digital (`listarsvp`). A emissão tem lista própria, mais estreita.
export const PERFIS_RSVP = ['rsvp', 'gerentersvp', 'coordenadorrsvp', 'operadorrsvp'];
// Eventos abre e envia a solicitação, lê a malha, e aprova ou reprova a malha.
export const PERFIS_EVENTOS = ['evento', 'gerenteevento', 'gerenteeventointernacional'];
// Aéreo e Gerente Aéreo: montam, importam, editam e enviam a malha. Os dois só operam o evento que
// atendem; qualquer um assume o evento, e o gerente vincula outras pessoas (`useAtendimentoAereo`).
export const PERFIS_AEREO = ['aereo', 'gerenteaereo'];
export const PERFIL_GERENTE_AEREO = 'gerenteaereo';

// RSVP e admin: pedem alteração da malha e importam as opções. A solicitação em si também é de
// Eventos (`abrePedido`).
export const cuidaDoPedido = (perfil) => perfil === 'admin' || PERFIS_RSVP.includes(perfil);

// RSVP, Eventos e admin: abrem, editam, enviam, notificam, reabrem, cancelam e excluem a solicitação.
export const abrePedido = (perfil) => cuidaDoPedido(perfil) || PERFIS_EVENTOS.includes(perfil);

// ── Copiar e colar como numa planilha ──────────────────────────────────────────
// O Copiar da malha copia a LINHA inteira, e também põe no clipboard o texto dela como numa planilha:
// uma linha por opção, as células separadas por tabulação, nas colunas da visão de informações
// completas da planilha da malha, da Opção à Obs. — com a sigla, a cidade e o aeroporto de cada ponta,
// nada se perde no caminho até o Excel e de volta. Só esse texto volta pelo `copiaDoTexto`: a planilha
// do Modelo Cliente não tem a sigla nem as taxas, e colada pareceria a opção inteira sem ser. Quem
// escolhe o lado é o Colar: Ida, Volta ou Ida & Volta (`camposDoColar`).
// A ordem é a das colunas da visão completa (`COLS` da PlanilhaMalhaAerea, sem as só do compacto).
const COLUNAS_COMPLETAS = [
    'opcao',
    'idaOrigemIata', 'idaOrigemCidade', 'idaOrigemAeroporto', 'idaDestinoIata', 'idaDestinoCidade', 'idaDestinoAeroporto',
    'idaData', 'idaCia', 'idaVoo', 'idaSaida', 'idaChegada',
    'retOrigemIata', 'retOrigemCidade', 'retOrigemAeroporto', 'retDestinoIata', 'retDestinoCidade', 'retDestinoAeroporto',
    'retData', 'retCia', 'retVoo', 'retSaida', 'retChegada',
    'tarifaIda', 'taxasIda', 'tarifaRet', 'taxasRet', 'total', 'tktPessoa', 'tktGrupo', 'numParticipantes', 'obs',
];
// Célula com tabulação, quebra de linha ou aspas vai entre aspas, como o Excel escreve.
const celulaDoTexto = (v) => {
    const s = String(v ?? '');
    return /[\t\n"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// `linhas`: as opções como a tela as mostra, com o total e o TKT já calculados.
export const textoDasLinhas = (linhas) =>
    linhas.map(op => COLUNAS_COMPLETAS.map(c => celulaDoTexto(op[c])).join('\t')).join('\r\n');

// Os lados que a cópia traz, decididos pelo conteúdo e não por marca: o lado com algum campo preenchido
// em alguma linha. A linha inteira copiada sem a volta é um lado só, como a cópia só da ida.
export const ladosDaCopia = (fonte) => ['ida', 'ret'].filter(l =>
    (fonte?.itens ?? []).some(item => keysLado(l).some(k => String(item[k] ?? '').trim() !== '')));

// Os campos que o Colar leva para uma linha: os dos lados escolhidos. Da cópia de um lado só
// (`ladosDaCopia`), aquele lado vai para o escolhido, campo a campo. Campo que não veio não apaga o da
// linha.
export function camposDoColar(item, lados, fonte) {
    const vieram = ladosDaCopia(fonte);
    const out = {};
    lados.forEach(l => {
        const de = vieram.length === 1 ? vieram[0] : l;
        const daqui = keysLado(de);
        keysLado(l).forEach((k, i) => { if (daqui[i] in item) out[k] = item[daqui[i]]; });
    });
    return out;
}

// O texto de uma planilha em linhas de células, com a célula entre aspas do Excel (a que tem quebra
// de linha). Linha toda vazia sai: o Excel põe uma quebra no fim do que copia.
export function lerTextoDePlanilha(texto) {
    const s = String(texto ?? '').replace(/\r\n?/g, '\n');
    const linhas = [];
    let linha = [];
    let celula = '';
    let entreAspas = false;
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (entreAspas) {
            if (c === '"' && s[i + 1] === '"') { celula += '"'; i++; }
            else if (c === '"') entreAspas = false;
            else celula += c;
        } else if (c === '"' && celula === '') entreAspas = true;
        else if (c === '\t') { linha.push(celula); celula = ''; }
        else if (c === '\n') { linha.push(celula); linhas.push(linha); linha = []; celula = ''; }
        else celula += c;
    }
    if (celula !== '' || linha.length) { linha.push(celula); linhas.push(linha); }
    return linhas.filter(l => l.some(v => v.trim() !== ''));
}

// O que veio do clipboard, no formato da cópia da malha ({ itens }), ou null quando o texto não tem esse
// formato. Quem diz o formato é a ESTRUTURA, e não os valores: as 32 colunas da cópia da malha
// (`textoDasLinhas`) em todas as linhas, com sigla, cidade e aeroporto de cada ponta. Os lados que
// vieram o Colar decide pelo conteúdo (`ladosDaCopia`). Cidade vazia fica fora do item, e o Colar não
// apaga a da linha.
const CIDADES = ['idaOrigemCidade', 'idaDestinoCidade', 'retOrigemCidade', 'retDestinoCidade'];
export function copiaDoTexto(texto) {
    const linhas = lerTextoDePlanilha(texto);
    if (!linhas.length || linhas.some(l => l.length !== COLUNAS_COMPLETAS.length)) return null;
    const itens = linhas.map(celulas => {
        const item = Object.fromEntries(COLUNAS_COMPLETAS.map((c, k) => [c, celulas[k].trim()]));
        CIDADES.forEach(k => { if (item[k] === '') delete item[k]; });
        return item;
    });
    return { itens };
}

// ── Importação das opções, por lado ─────────────────────────────────────────────
// Espelho das travas de `EventoAereoPortacaoService::aplicar` — o back recusa de todo jeito.

export const LADOS_IMPORTACAO = ['ida', 'retorno'];

const PILL_LADO = {
    importada:  { rotulo: 'No evento', cor: 'is-ok', titulo: 'Já está nas opções do evento, igual à malha.' },
    divergente: { rotulo: 'Diverge', cor: 'is-warn', titulo: 'Está no evento, mas a malha mudou depois. Importar de novo atualiza o voo.' },
    // "Alterada NO EVENTO" e não só "Alterada": esta pill aparece dentro da planilha da MALHA, e
    // ali "alterada" se confunde com a malha ter sido alterada. O que mudou foi a opção, na tela
    // de opções do evento. Mesma cor de atenção das outras "Alterada" da interface.
    alterada:   { rotulo: 'Alterada no evento', cor: 'is-warn', titulo: 'Alterada na tela de opções do evento: não volta a ser da malha. Continua no evento.' },
    duplicata:  { rotulo: 'Manual igual', cor: 'is-warn', titulo: 'O evento já tem uma opção manual com o mesmo voo: importar cria outra ao lado.' },
    // `icone`: nome, não componente — este arquivo é domínio puro, sem React; a planilha desenha.
    internacional: { rotulo: 'Internacional', cor: 'is-info', icone: 'globo', titulo: 'Tarifa internacional de ida e volta: a volta entra junto com a ida.' },
};

/**
 * Cada lado de uma opção da prévia da Importação (`previa.malhas[].opcoes[]`): se a malha tem o
 * lado, se dá para marcar e a pill que explica por que não. Importada e alterada não se marcam;
 * divergente só sem escolha de participante naquele lado, que trava o voo.
 */
export function ladosDaImportacao(o) {
    const lados = {};
    LADOS_IMPORTACAO.forEach(lado => {
        const l = o?.lados?.[lado];
        if (!l?.tem) {
            lados[lado] = { tem: false, pode: false };
            return;
        }
        if (o.estado === 'alterada' || l.estado === 'alterada') {
            lados[lado] = { tem: true, pode: false, ...PILL_LADO.alterada };
        } else if (l.estado === 'importada') {
            lados[lado] = { tem: true, pode: false, ...PILL_LADO.importada };
        } else if (l.estado === 'divergente') {
            lados[lado] = Number(l.escolhas) > 0
                ? { tem: true, pode: false, ...PILL_LADO.divergente,
                    titulo: `Escolhida por participante na ${lado === 'ida' ? 'ida' : 'volta'}: o voo não muda. Para liberar, resete a etapa de aéreo de quem escolheu.` }
                : { tem: true, pode: true, ...PILL_LADO.divergente };
        } else if (o.estado === 'duplicata') {
            lados[lado] = { tem: true, pode: true, ...PILL_LADO.duplicata };
        } else {
            lados[lado] = { tem: true, pode: true };
        }
    });
    // Tarifa internacional é do PAR ida e volta: a ida responde pelos dois, e a volta mostra o
    // porquê no lugar do checkbox. O par se marca se algum lado tem o que importar e nenhum está
    // preso — lado já no evento e igual à malha não prende, só não tem o que fazer; alterado no
    // evento ou escolhido por participante prende. O back recusa lado solto (`motivoLados`).
    if (o?.nacionalidade === 'internacional' && lados.ida.tem && lados.retorno.tem) {
        const preso = (lado) => !lados[lado].pode && o.lados?.[lado]?.estado !== 'importada';
        const pode = (lados.ida.pode || lados.retorno.pode) && !preso('ida') && !preso('retorno');
        lados.ida = { ...lados.ida, pode };
        lados.retorno = { tem: true, pode: false, par: true, ...PILL_LADO.internacional };
    }
    return lados;
}

/**
 * Seleção da Importação ({ [id da opção da malha]: { ida, retorno } }) → ações `portar` do back,
 * só com os lados que ainda se marcam na prévia atual. `infoPorId`: Map id → opção da prévia.
 */
/**
 * O contrato da coluna de importar da planilha: o que cada lado de cada opção mostra, e o que
 * acontece ao marcar um lado ou todos de um lado.
 *
 * As DUAS telas que importam — a da Malha e a Importação das Opções — montavam este objeto por
 * conta própria, linha por linha igual. Cópia assim não avisa quando diverge: ela aparece no dia
 * em que uma das duas deixa de marcar e ninguém sabe qual está certa.
 *
 * Não é hook. Recebe o estado e o setter de quem chama, porque quem guarda os marcados é a tela —
 * numa é o bloco de uma malha, na outra são todas as malhas aprovadas do evento.
 */
export function selecaoDeImportacao({ opcoes, infoPorOpcao, marcados, setMarcados }) {
    const ladosDe = (op) => {
        const info = op?.id ? infoPorOpcao.get(String(op.id)) : null;
        if (!info) return null;
        const lados = ladosDaImportacao(info);
        LADOS_IMPORTACAO.forEach(lado => { lados[lado].marcado = !!marcados[String(op.id)]?.[lado]; });
        // No par internacional a volta acende junto com a ida: é ela que vai junto.
        if (lados.retorno?.par) lados.retorno.marcado = lados.ida.marcado;
        return lados;
    };
    return {
        lados: ladosDe,
        onAlternar: (op, lado) => setMarcados(prev => {
            const id = String(op.id);
            return { ...prev, [id]: { ...prev[id], [lado]: !prev[id]?.[lado] } };
        }),
        onAlternarTodos: (lado, marcar) => setMarcados(prev => {
            const next = { ...prev };
            (opcoes ?? []).forEach(op => {
                if (ladosDe(op)?.[lado]?.pode) next[String(op.id)] = { ...next[String(op.id)], [lado]: marcar };
            });
            return next;
        }),
    };
}

// A seleção da importação guardada por MALHA no navegador. Sobrevive a recarregar a tela e à ida
// da malha alterada para Eventos: quando ela volta aprovada, o que foi marcado está lá. Sem onde
// guardar (aba privada, bloqueio), a seleção vale só enquanto a tela está aberta.
const chaveSelecao = (idMalha) => `malhaAerea:importar:${idMalha}`;
export function lerSelecaoDaMalha(idMalha) {
    try {
        const v = JSON.parse(window.localStorage.getItem(chaveSelecao(idMalha)) || '{}');
        return v && typeof v === 'object' ? v : {};
    } catch (e) {
        return {};
    }
}
export function guardarSelecaoDaMalha(idMalha, marcados) {
    try {
        const marcadas = Object.fromEntries(Object.entries(marcados ?? {}).filter(([, l]) => l?.ida || l?.retorno));
        if (Object.keys(marcadas).length) window.localStorage.setItem(chaveSelecao(idMalha), JSON.stringify(marcadas));
        else window.localStorage.removeItem(chaveSelecao(idMalha));
    } catch (e) { /* sem onde guardar */ }
}

/** Quantos LADOS as ações cobrem: a opção com ida e volta marcadas conta dois. */
export const contarLadosMarcados = (acoes) => (acoes ?? []).reduce((n, a) => n + a.lados.length, 0);

export function acoesDeImportacao(marcados, infoPorId, acao = 'portar') {
    return Object.entries(marcados)
        .map(([id, marcadosLado]) => {
            const permitidos = ladosDaImportacao(infoPorId.get(String(id)));
            const lados = LADOS_IMPORTACAO.filter(lado => marcadosLado?.[lado] && permitidos[lado].pode);
            // Par internacional: a ida leva a volta.
            if (permitidos.retorno?.par && lados.includes('ida')) lados.push('retorno');
            return { acao, malhaOpcao: String(id), lados };
        })
        .filter(a => a.lados.length > 0);
}

// ── Origens coladas na solicitação ─────────────────────────────────────────────
// O RSVP cadastra as origens de uma vez, colando de onde elas já estão. Separam uma origem
// da outra: linha do Excel (tab), coluna do Excel e lista (quebra de linha), vírgula, ponto
// e vírgula, barra vertical e marcadores de lista (•, ·). Hífen e barra NÃO separam: "São
// Paulo - Guarulhos" e "Rio de Janeiro/GIG" são uma origem só.
// "Salvador, BA": a vírgula antes de sigla de UF é do nome, não quebra.
const ANTES_DE_UF = /^\s*[A-Z]{2}(?![\p{L}\d])/u;
// Abreviações comuns em nome de cidade e aeroporto ("Sto. André", "Int. de Guarulhos"), sem
// acento e em minúscula.
const ABREVIACOES = new Set(['sto', 'sta', 'int', 'intl', 'av', 'dr', 'gal', 'mal', 'pres', 'gov', 'cel', 'sen', 'dep', 'nsa', 'sr', 'sra', 'prof', 'jd', 'pq', 'vl', 'aerop']);

// O ponto quebra no fim de um nome, antes de maiúscula ou do fim do texto. Depois de uma letra
// só ("S. J. dos Campos") ou de abreviação, é do nome.
const pontoQuebra = (texto, i) => {
    if (!/^\s*(?:\p{Lu}|$)/u.test(texto.slice(i + 1))) return false;
    const antes = texto.slice(0, i);
    if (/\)\s*$/.test(antes)) return true;
    const palavra = (antes.match(/\p{L}+$/u) ?? [''])[0];
    return palavra.length > 1 && !ABREVIACOES.has(palavra.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase());
};

// Tipos de separador das origens coladas, do mais forte ao mais fraco. Depois de textoDeOrigens
// a quebra de linha é sempre "\n". `quebra` diz se a ocorrência na posição `i` separa de fato;
// sem ela, toda ocorrência separa.
export const SEPARADORES_ORIGEM = [
    { chave: 'linha', rotulo: 'Linhas', caractere: /\n/g },
    { chave: 'tab', rotulo: 'Colunas', caractere: /\t/g },
    { chave: 'virgula', rotulo: 'Vírgula', caractere: /,/g, quebra: (texto, i) => !ANTES_DE_UF.test(texto.slice(i + 1)) },
    { chave: 'pontoVirgula', rotulo: 'Ponto e vírgula', caractere: /;/g },
    { chave: 'doisPontos', rotulo: 'Dois-pontos', caractere: /:/g },
    { chave: 'ponto', rotulo: 'Ponto', caractere: /\./g, quebra: pontoQuebra },
    { chave: 'barra', rotulo: 'Barra ( | )', caractere: /\|/g },
    { chave: 'marcador', rotulo: 'Marcador ( • )', caractere: /[•·]/g },
];
// Numeração e marcador no começo de item de lista colada: "1. ", "2) ", "- ", "* ", "• ".
const MARCADOR_ITEM = /^(?:\d{1,3}[.)]\s+|[-*]\s+|[•·]\s*)/;

// Sem as pontas e sem o marcador do começo de cada linha: a quebra de linha final do Excel e o
// "• " de uma lista não separam nada, e contados como separador fariam perguntar à toa.
const textoDeOrigens = (texto) => String(texto ?? '').trim()
    .split(/\r\n|\r|\n/)
    .map(linha => linha.trim().replace(MARCADOR_ITEM, ''))
    .join('\n');

// O primeiro valor do grupo se junta a cada um dos outros assim.
export const JUNCAO_GRUPO = ' - ';

// As posições que quebram de fato, por tipo.
const quebrasReais = (limpo) => new Map(SEPARADORES_ORIGEM.map(s => [
    s.chave,
    [...limpo.matchAll(s.caractere)].map(m => m.index).filter(i => !s.quebra || s.quebra(limpo, i)),
]));

// Marcas no lugar das quebras escolhidas: GRUPO nas de agrupar, VALOR nas de separar. A
// ocorrência que não quebra, ou que não foi escolhida, fica no nome.
const GRUPO = '\u0001';
const VALOR = '\u0002';
const marcarQuebras = (limpo, quebras, separar, agrupar) => {
    // Índices do matchAll são de unidade UTF-16: split(''), não o spread por caractere.
    const letras = limpo.split('');
    separar.forEach(chave => (quebras.get(chave) ?? []).forEach(i => { letras[i] = VALOR; }));
    agrupar.forEach(chave => (quebras.get(chave) ?? []).forEach(i => { letras[i] = GRUPO; }));
    return letras.join('');
};

// Aspas vêm do Excel quando a célula tem quebra de linha; a pontuação nas pontas ("Congonhas."),
// o marcador de lista e o espaço repetido também saem.
const limparPedaco = (bruto) => bruto
    .replace(/^["'\s.:,;]+|["'\s.:,;]+$/g, '')
    .replace(MARCADOR_ITEM, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Os tipos de separador que quebram de fato no texto, com quantas vezes — menos os que não mudam
 * nada. O ponto no fim de cada linha e o ponto e vírgula colado na tabulação saem: um tipo sai
 * quando outro mais forte (antes em SEPARADORES_ORIGEM) já separa exatamente nos mesmos lugares.
 */
export function tiposDeSeparador(texto) {
    const limpo = textoDeOrigens(texto);
    const quebras = quebrasReais(limpo);
    // Compara já com a quantidade tirada: "São Paulo | 12" por linha e por linha + coluna dão o
    // mesmo, e a coluna não precisa aparecer.
    const itens = (chaves) => comQuantidades(marcarQuebras(limpo, quebras, chaves, []).split(VALOR).map(limparPedaco).filter(Boolean));
    const presentes = SEPARADORES_ORIGEM
        .map(s => ({ chave: s.chave, rotulo: s.rotulo, quantos: quebras.get(s.chave).length }))
        .filter(s => s.quantos > 0);
    return presentes.filter((t, i) => !presentes.slice(0, i).some(forte => {
        const so = itens([forte.chave]);
        const juntos = itens([forte.chave, t.chave]);
        return so.length === juntos.length
            && so.every((item, k) => item.nome === juntos[k].nome && item.quantidade === juntos[k].quantidade);
    }));
}

// Quantidade de passageiros que vem junto da origem colada. Número sozinho num valor ("12",
// "(12)", "12 pax") é a quantidade do vizinho; no fim do nome ("São Paulo 12", "São Paulo (12)",
// "São Paulo - 12") é a do próprio nome. "Guarulhos T2" não tem: o número precisa de espaço ou
// pontuação antes.
const SO_QUANTIDADE = /^\(?(\d{1,4})\)?(?:\s*(?:pax|passageiros?|pessoas?))?$/i;
const QUANTIDADE_NO_FIM = /^(.*?\p{L}.*?)[\s–:=(-]+(\d{1,4})\)?(?:\s*(?:pax|passageiros?|pessoas?))?$/iu;

/**
 * Valores limpos → [{ nome, quantidade }]. O número sozinho vai para o valor de antes, se ele
 * ainda não tem quantidade ("São Paulo | 12"); senão, para o próximo ("12 | São Paulo").
 */
function comQuantidades(valores) {
    const itens = [];
    let pendente = null;
    valores.forEach(valor => {
        const so = valor.match(SO_QUANTIDADE);
        if (so) {
            const anterior = itens[itens.length - 1];
            if (anterior && anterior.quantidade === null) anterior.quantidade = Number(so[1]);
            else pendente = Number(so[1]);
            return;
        }
        const noFim = valor.match(QUANTIDADE_NO_FIM);
        itens.push(noFim
            ? { nome: noFim[1].trim(), quantidade: Number(noFim[2]) }
            : { nome: valor, quantidade: pendente });
        pendente = null;
    });
    return itens;
}

/**
 * Pergunta com mais de um tipo, ou com dois-pontos: "Grupo: valor, valor" pede agrupar, e a
 * prévia mostra como ficou.
 */
export const precisaEscolherSeparador = (tipos) => tipos.length > 1 || tipos.some(t => t.chave === 'doisPontos');

/**
 * O que já vem marcado na pergunta, `{ separar, agrupar }`.
 *
 * Com dois-pontos é o padrão "São Paulo: Guarulhos, Congonhas. Bahia: Salvador.": o grupo
 * termina na quebra de linha, no ponto ou no ponto e vírgula (o primeiro que houver, nessa
 * ordem; sem nenhum, a linha única é o grupo), e o resto separa os valores.
 *
 * Sem dois-pontos, todos separam: o que não quebra de fato ("Salvador, BA", "Sto. André") já
 * ficou de fora em tiposDeSeparador.
 */
export function separadoresSugeridos(tipos) {
    const chaves = tipos.map(t => t.chave);
    if (chaves.includes('doisPontos')) {
        const fimDoGrupo = ['linha', 'ponto', 'pontoVirgula'].find(c => chaves.includes(c)) ?? 'linha';
        return { separar: chaves.filter(c => c !== fimDoGrupo), agrupar: [fimDoGrupo] };
    }
    return { separar: chaves, agrupar: [] };
}

/**
 * O que a pergunta mostra: os tipos, na ordem de SEPARADORES_ORIGEM, e a sugestão marcada. Um
 * tipo sugerido que não aparece no texto (a linha única agrupada) entra na lista com zero, senão
 * ficaria marcado sem que desse para desmarcar.
 */
export function escolhaDeSeparadores(texto) {
    const tipos = tiposDeSeparador(texto);
    const sugestao = separadoresSugeridos(tipos);
    const quantos = new Map(tipos.map(t => [t.chave, t.quantos]));
    const visiveis = new Set([...quantos.keys(), ...sugestao.separar, ...sugestao.agrupar]);
    return {
        tipos: SEPARADORES_ORIGEM
            .filter(s => visiveis.has(s.chave))
            .map(s => ({ chave: s.chave, rotulo: s.rotulo, quantos: quantos.get(s.chave) ?? 0 })),
        ...sugestao,
    };
}
// Origem e destino: ao menos 2 letras — cidade, aeroporto, IATA ou sigla de UF.
export const LOCAL_MIN = 2;

// Placeholder dos campos de origem e destino (solicitação, trecho e edição da malha). O hífen
// depois da sigla é o que a faz ser lida como sigla (siglasDoTrecho): só espaço não isola.
// O que o campo de local aceita, e a forma longa com exemplo. Em campo estreito (a ponta com as
// duas pernas abertas) vale a curta: o exemplo não cabe e some antes do que importa.
// Cidade, Aeroporto e IATA em maiúscula porque são as três coisas que o campo aceita — categorias,
// não prosa. "todos" fica minúsculo: é linguagem corrente, não uma palavra que o casador entenda.
export const LOCAL_ACEITO = 'Cidade, Aeroporto, IATA ou todos';
export const EXEMPLO_LOCAL = `${LOCAL_ACEITO} — ex.: SAO São Paulo Guarulhos`;

// Chave de comparação: sem acento, sem caixa e com espaço único.
export const chaveOrigem = (texto) => String(texto ?? '')
    .normalize('NFD').replace(/\p{M}/gu, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Texto colado ou digitado → origens. Devolve as novas na ordem em que vieram, e à parte as
 * que já existiam (`existentes`, ou repetidas na própria colagem) e as curtas demais, para a
 * tela dizer o que ficou de fora.
 *
 * `tipos`: as chaves de SEPARADORES_ORIGEM que separam um valor do outro; sem ele, todas.
 * `agrupar`: as que delimitam GRUPOS. Dentro do grupo, o primeiro valor se repete na frente de
 * cada um dos outros, juntado com JUNCAO_GRUPO: agrupada por linha, a linha do Excel
 * "São Paulo | Congonhas | Guarulhos" (uma coluna cada) vira "São Paulo - Congonhas" e
 * "São Paulo - Guarulhos", o formato que o casador de local lê como cidade e aeroporto. Grupo
 * com um valor só entra sozinho.
 *
 * Só a ocorrência que quebra de fato separa ou agrupa (vírgula antes de UF e ponto de
 * abreviação ficam no nome), e o separador fora das duas listas também fica.
 *
 * A quantidade de passageiros que vier junto (comQuantidades) sai do nome e vai para
 * `quantidades`, pelo nome final da origem nova. Origem que já está na lista e vem com outra
 * quantidade não se repete: vai para `atualizacoes` ({ chave, nome, quantidade }), e a tela troca
 * a quantidade dela.
 *
 * `existentes`: os nomes, ou { origem, quantidadePassageiros } para a quantidade igual à atual não
 * contar como atualização.
 */
export function separarOrigens(texto, existentes = [], tipos = null, agrupar = []) {
    const atuais = new Map((existentes ?? []).map(e => (typeof e === 'string'
        ? [chaveOrigem(e), undefined]
        : [chaveOrigem(e.origem), e.quantidadePassageiros ?? null])));
    const vistas = new Set(atuais.keys());
    const atualizadas = new Set();
    const resultado = { novas: [], repetidas: [], curtas: [], quantidades: {}, atualizacoes: [] };
    const agrupa = agrupar ?? [];
    const separa = (tipos ?? SEPARADORES_ORIGEM.map(s => s.chave)).filter(c => !agrupa.includes(c));
    const limpo = textoDeOrigens(texto);
    marcarQuebras(limpo, quebrasReais(limpo), separa, agrupa).split(GRUPO).forEach(grupo => {
        const itens = comQuantidades(grupo.split(VALOR).map(limparPedaco).filter(Boolean));
        // Agrupado, a quantidade é de cada valor, não do grupo: a do primeiro não se repete.
        const origens = agrupa.length && itens.length > 1
            ? itens.slice(1).map(item => ({ nome: `${itens[0].nome}${JUNCAO_GRUPO}${item.nome}`, quantidade: item.quantidade }))
            : itens;
        origens.forEach(({ nome, quantidade }) => {
            if (nome.length < LOCAL_MIN) {
                resultado.curtas.push(nome);
                return;
            }
            const chave = chaveOrigem(nome);
            if (atuais.has(chave) && !atualizadas.has(chave) && quantidade !== null && quantidade !== atuais.get(chave)) {
                atualizadas.add(chave);
                resultado.atualizacoes.push({ chave, nome, quantidade });
                return;
            }
            if (vistas.has(chave)) {
                resultado.repetidas.push(nome);
                return;
            }
            vistas.add(chave);
            resultado.novas.push(nome);
            if (quantidade !== null) resultado.quantidades[nome] = quantidade;
        });
    });
    return resultado;
}
